import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";
import {
  LayoutDashboard, Users, Mail, Calendar, Settings, Target, Zap, TrendingUp,
  LogOut, Upload, Brain, Search, ChevronRight, ExternalLink, RefreshCw,
  Database, UserCog, Download, ArrowUpRight, Building2, Globe,
  CheckCircle, XCircle, Clock, AlertTriangle, Eye, Copy, ChevronDown,
  Menu, X, Plus, Shield, MessageSquare, Send, UserCheck, BarChart3
} from "lucide-react";

const API = axios.create({ baseURL: "/api" });

function setToken(t) { localStorage.setItem("yt", t); API.defaults.headers.common["Authorization"] = `Bearer ${t}`; }
function getToken() { const t = localStorage.getItem("yt"); if (t) API.defaults.headers.common["Authorization"] = `Bearer ${t}`; return t; }
function clearToken() { localStorage.removeItem("yt"); delete API.defaults.headers.common["Authorization"]; }

// ============ CONSTANTS ============
const TRACK_LABELS = { C: "Consultancy", I: "Investments", A: "Accounting", T: "Transaction Advisory", F: "Family Office", E: "Execution" };
const TRACK_COLORS = { C: "#1B3A4B", I: "#2563EB", A: "#0D9668", T: "#0E7490", F: "#D97706", E: "#7C3AED" };
const GEO_LABELS = { "1": "UAE", "2": "GCC", "3": "Lebanon", "4": "Africa", "5": "International" };
const READY_LABELS = { pending: "Pending", ready: "Ready", needs_review: "Needs Review", junk: "Junk", duplicate: "Duplicate" };
const READY_BADGE = { pending: "gray", ready: "green", needs_review: "amber", junk: "red", duplicate: "gray" };
const TAG_COLORS = { "UAE C-Suite": "#1B3A4B", "GCC Investors": "#2563EB", "Lebanon CEOs": "#0D9668", "UK Family Offices": "#D97706", "UK VC-PE": "#7C3AED", "UK-USA Accounting": "#0E7490", "Travel": "#F59E0B", "Turkey Financial": "#6B7280" };
const DEFAULT_TAGS = ["UAE C-Suite", "GCC Investors", "Lebanon CEOs", "UK Family Offices", "UK VC-PE", "UK-USA Accounting", "Travel"];
const STATUS_BADGE = {
  new: "gray", imported: "gray", enriching: "gray", enriched: "blue", generated: "blue",
  sequence_active: "blue", engaged: "amber", replied_interested: "green",
  replied_not_interested: "red", replied_not_now: "amber", replied_question: "amber",
  replied_ooo: "gray", meeting_scheduled: "green", meeting_completed: "green",
  proposal_sent: "navy", won: "green", lost: "red", nurture: "gray", unsubscribed: "red", bounced: "red", handed_off: "amber"
};

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast toast-${type}`}>{message}</div>;
}

function useToast() {
  const [toast, setToast] = useState(null);
  const show = (message, type = "info") => setToast({ message, type, key: Date.now() });
  const el = toast ? <Toast key={toast.key} message={toast.message} type={toast.type} onClose={() => setToast(null)} /> : null;
  return [show, el];
}

function copyToClipboard(text, showToast) {
  navigator.clipboard.writeText(text).then(() => showToast && showToast("Copied", "success"));
}

// ============ LOGIN ============
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await API.post("/auth/login", { email, password: pass });
      setToken(data.token);
      onLogin(data.user);
    } catch { setErr("Invalid credentials"); setLoading(false); }
  };
  return (
    <div className="login-page">
      <form className="login-card" onSubmit={submit}>
        <img src="/logo.png" alt="yemnak" style={{ height: 48, marginBottom: 8 }} />
        <div className="login-subtitle">Sales Engine Command Center</div>
        {err && <div className="login-error">{err}</div>}
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-input" type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="Enter password" required />
        </div>
        <button className="btn btn-primary w-full btn-lg" type="submit" disabled={loading}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

// ============ SIDEBAR ============
function Sidebar({ user, onLogout, isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const nav = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { section: "Pipeline" },
    { path: "/prospects", icon: Users, label: "Prospects" },
    { path: "/replies", icon: MessageSquare, label: "Replies" },
    { path: "/campaigns", icon: Target, label: "Campaigns" },
    { path: "/outreach", icon: Send, label: "Outreach" },
    { path: "/analytics", icon: BarChart3, label: "Analytics" },
    { path: "/meetings", icon: Calendar, label: "Meetings" },
    { section: "Automation" },
    { path: "/ai-engine", icon: Brain, label: "AI Engine" },
    { path: "/smart-import", icon: Database, label: "Smart Import" },
    { path: "/import", icon: Upload, label: "Import Data" },
    { section: "System" },
    { path: "/settings", icon: Settings, label: "Settings" },
    { path: "/users", icon: UserCog, label: "Users" },
  ];
  return (
    <div className={`sidebar ${isOpen ? "open" : ""}`}>
      <div className="sidebar-logo">
        <img src="/logo-white.png" alt="yemnak" style={{ height: 42, marginBottom: 6 }} />
        <span>Sales Engine</span>
        <button className="sidebar-close" onClick={onClose}><X size={20} /></button>
      </div>
      <div className="sidebar-nav">
        {nav.map((item, i) => item.section ? (
          <div key={i} className="nav-section">{item.section}</div>
        ) : (
          <div key={i} className={`nav-item ${location.pathname === item.path ? "active" : ""}`} onClick={() => { navigate(item.path); onClose(); }}>
            <item.icon size={17} />
            {item.label}
          </div>
        ))}
      </div>
      <div className="sidebar-footer">
        <div className="sidebar-user">{user?.name || user?.email}</div>
        <div className="nav-item" onClick={onLogout}><LogOut size={15} /> Sign Out</div>
      </div>
    </div>
  );
}

// ============ DASHBOARD ============
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToast, toastEl] = useToast();
  const [domainHealth, setDomainHealth] = useState([]);
  const [dhLoading, setDhLoading] = useState(false);
  const [dhChecking, setDhChecking] = useState(false);
  const [liChanges, setLiChanges] = useState([]);
  const [liMonitoring, setLiMonitoring] = useState(false);
  const [autoPilot, setAutoPilot] = useState("on");
  const [apToggling, setApToggling] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  // SN52: manual meetings counter — initialized from stats, kept in sync via +1/-1 endpoints.
  const [meetingsCount, setMeetingsCount] = useState(null);

  useEffect(() => {
    if (stats?.hero_strip?.total_meetings !== undefined && meetingsCount === null) {
      setMeetingsCount(stats.hero_strip.total_meetings);
    }
  }, [stats, meetingsCount]);

  const incrementMeetings = async () => {
    try {
      const r = await API.post("/dashboard/meetings/increment");
      setMeetingsCount(r.data.count);
    } catch (e) {
      console.error("Failed to increment meetings:", e);
    }
  };

  const decrementMeetings = async () => {
    try {
      const r = await API.post("/dashboard/meetings/decrement");
      setMeetingsCount(r.data.count);
    } catch (e) {
      console.error("Failed to decrement meetings:", e);
    }
  };

  const loadDomainHealth = useCallback(() => {
    API.get("/health/domains").then(r => setDomainHealth(r.data.domains || [])).catch(() => {});
  }, []);

  const toggleAutoPilot = async () => {
    setApToggling(true);
    const newVal = autoPilot === "on" ? "off" : "on";
    try {
      await API.post("/settings/auto-pilot", { enabled: newVal === "on" });
      setAutoPilot(newVal);
      showToast(`Auto-Pilot ${newVal === "on" ? "enabled" : "disabled"}`, "success");
    } catch (e) { showToast("Failed to toggle auto-pilot", "error"); }
    setApToggling(false);
  };

  useEffect(() => {
    API.get("/dashboard").then(r => {
      setStats(r.data);
      setAutoPilot(r.data.auto_pilot || "on");
      setLoading(false);
      API.get("/dashboard/serious-leads")
        .then(r2 => setStats(prev => prev ? { ...prev, serious_leads: r2.data } : prev))
        .catch(() => {});
    }).catch(() => setLoading(false));
    API.get("/dashboard/pipeline").then(r => setPipeline(r.data)).catch(() => {});
    loadDomainHealth();
    API.get("/linkedin/changes").then(r => {
      const now = Date.now();
      const week = 7 * 24 * 60 * 60 * 1000;
      setLiChanges((r.data.changes || []).filter(c => (now - new Date(c.detected_at).getTime()) < week));
    }).catch(() => {});
  }, [loadDomainHealth]);

  if (loading) return <div className="main-content"><p className="text-tertiary">Loading dashboard...</p></div>;
  if (!stats) return <div className="main-content"><p className="text-red">Failed to load dashboard</p></div>;

  const emailStats = stats.email_stats || {};
  const openRate = emailStats.sent > 0 ? ((emailStats.opened / emailStats.sent) * 100).toFixed(1) : "0";
  const replyRate = emailStats.sent > 0 ? ((emailStats.replied / emailStats.sent) * 100).toFixed(1) : "0";
  const trackData = (stats.by_track || []).map(t => ({ name: TRACK_LABELS[t.service_track] || t.service_track, value: t.count, fill: TRACK_COLORS[t.service_track] || "#94A3B8" }));
  const statusData = (stats.by_status || []).slice(0, 8).map(s => ({ name: s.status?.replace(/_/g, " ") || "unknown", count: s.count }));

  return (
    <div className="main-content">
      {toastEl}
      {stats?.hero_strip && (
        <div style={{ marginBottom: 24, padding: "24px 28px", background: "linear-gradient(135deg, #1B3A4B 0%, #2C5364 100%)", borderRadius: 12, color: "white" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, opacity: 0.65 }}>Yemnak Sales Engine</div>
              <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                Live since {new Date(stats.hero_strip.launch_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            </div>
            <div style={{ fontSize: 11, opacity: 0.65, textAlign: "right" }}>
              Last refresh:<br/>
              {stats.hero_strip.smartlead_last_refresh ? new Date(stats.hero_strip.smartlead_last_refresh).toLocaleString() : "Pending..."}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 20 }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>Days Running</div>
              <div style={{ fontSize: 36, fontWeight: 600, marginTop: 8 }}>{stats.hero_strip.days_running}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>Total Prospects</div>
              <div style={{ fontSize: 36, fontWeight: 600, marginTop: 8 }}>{(stats.hero_strip.total_prospects || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>Emails Sent</div>
              <div style={{ fontSize: 36, fontWeight: 600, marginTop: 8 }}>{(stats.hero_strip.total_sent || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>Replies</div>
              <div style={{ fontSize: 36, fontWeight: 600, marginTop: 8 }}>{(stats.hero_strip.total_replies || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.6 }}>Meetings</div>
              <div style={{ fontSize: 36, fontWeight: 600, marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
                <span>{(meetingsCount !== null ? meetingsCount : (stats.hero_strip.total_meetings || 0)).toLocaleString()}</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button
                    onClick={incrementMeetings}
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      color: "white",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontWeight: 600,
                      lineHeight: 1.2
                    }}
                    title="Add a meeting"
                  >+1</button>
                  <button
                    onClick={decrementMeetings}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      color: "white",
                      borderRadius: 4,
                      padding: "2px 8px",
                      fontSize: 11,
                      cursor: "pointer",
                      fontWeight: 600,
                      lineHeight: 1.2
                    }}
                    title="Undo last increment (misclick)"
                  >-1</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="page-header">
        <div>
          <h2>Command Center</h2>
          <div className="page-subtitle">Real-time pipeline overview</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={async () => { try { await API.get("/digest/send"); showToast("Digest sent", "success"); } catch(e) { showToast(e.response?.data?.error || "Failed", "error"); } }}><Mail size={14} /> Send Digest</button>
          <button className="btn btn-secondary btn-sm" onClick={() => window.location.reload()}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* Auto-Pilot Toggle */}
      <div className="card mb-6">
        <div className="card-body" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div>
              <div className="font-semibold" style={{ fontSize: 16 }}>Auto-Pilot</div>
              <div className="text-sm text-secondary" style={{ marginTop: 4, maxWidth: 480 }}>
                {autoPilot === "on"
                  ? "System is running automatically. Emails generate at 4AM GST, campaigns launch at 9AM GST Sun-Thu."
                  : "Automation paused. No auto-generate or auto-launch will run. Manual operation only."}
              </div>
            </div>
          </div>
          <div style={{ cursor: apToggling ? "wait" : "pointer", flexShrink: 0 }} onClick={!apToggling ? toggleAutoPilot : undefined}>
            <div style={{
              width: 56, height: 28, borderRadius: 14,
              background: autoPilot === "on" ? "#16a34a" : "#9ca3af",
              position: "relative", transition: "background 0.2s"
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: 11,
                background: "#fff", position: "absolute", top: 3,
                left: autoPilot === "on" ? 31 : 3,
                transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
              }} />
            </div>
            <div className="text-xs font-semibold" style={{ textAlign: "center", marginTop: 4, color: autoPilot === "on" ? "#16a34a" : "#9ca3af" }}>
              {autoPilot === "on" ? "ON" : "OFF"}
            </div>
          </div>
        </div>
      </div>

      {/* Last 24 Hours */}
      {stats.last_24h && (() => {
        const h = stats.last_24h;
        const timeAgo = (ts) => {
          if (!ts) return "never";
          const ms = Date.now() - new Date(ts + "Z").getTime();
          const hrs = Math.floor(ms / 3600000);
          if (hrs < 1) return Math.floor(ms / 60000) + "m ago";
          if (hrs < 24) return hrs + "h ago";
          return Math.floor(hrs / 24) + "d ago";
        };
        const isStale = (ts) => {
          if (!ts) return true;
          return (Date.now() - new Date(ts + "Z").getTime()) > 25 * 3600000;
        };
        const cronStatus = (run) => {
          if (!run) return { label: "DID NOT RUN", color: "#ea580c", bg: "#fff7ed" };
          if (isStale(run.timestamp)) return { label: "DID NOT RUN", color: "#ea580c", bg: "#fff7ed" };
          if (run.status === "failed") return { label: "FAILED", color: "#dc2626", bg: "#fef2f2" };
          return { label: "OK", color: "#16a34a", bg: "#f0fdf4" };
        };
        const genS = cronStatus(h.last_generate_run);
        const launchS = cronStatus(h.last_launch_run);
        const digestS = cronStatus(h.last_digest_run);
        const metricBox = (label, value, warn0) => {
          const isZero = value === 0;
          const color = warn0 && isZero ? "#ea580c" : isZero ? "#6b7280" : "#16a34a";
          return (
            <div key={label} style={{ textAlign: "center", padding: "10px 8px", borderRadius: 8, background: warn0 && isZero ? "#fff7ed" : isZero ? "#f9fafb" : "#f0fdf4", minWidth: 80 }}>
              <div style={{ fontSize: 22, fontWeight: 700, color }}>{value.toLocaleString()}</div>
              <div className="text-xs text-secondary" style={{ marginTop: 2 }}>{label}</div>
            </div>
          );
        };
        return (
          <div className="card mb-6">
            <div className="card-body">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div className="font-semibold" style={{ fontSize: 16 }}>Last 24 Hours</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="text-sm text-secondary">Auto-Pilot:</span>
                  <span style={{ fontWeight: 700, color: h.auto_pilot === "on" ? "#16a34a" : "#dc2626", fontSize: 14 }}>{h.auto_pilot === "on" ? "ON" : "OFF"}</span>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8, marginBottom: 16 }}>
                {metricBox("Sent", h.sent || h.emails_sent_24h, false)}
                {metricBox("Opened", h.emails_opened_24h, false)}
                {metricBox("Replies", h.new_replies_24h, false)}
                {metricBox("Generated", h.prospects_generated_24h, false)}
                {metricBox("Launched", h.prospects_launched_24h, false)}
                {metricBox("Bounces", h.bounces_24h, false)}
                {metricBox("Cron Runs", h.cron_runs.length, true)}
              </div>
              <div style={{ fontSize: 13 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Auto-Generate", s: genS, run: h.last_generate_run },
                    { label: "Auto-Launch", s: launchS, run: h.last_launch_run },
                    { label: "Digest", s: digestS, run: h.last_digest_run },
                  ].map(({ label, s, run }) => (
                    <div key={label} style={{ padding: "8px 12px", borderRadius: 6, background: s.bg, border: "1px solid " + s.color + "33" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span className="font-semibold">{label}</span>
                        <span style={{ color: s.color, fontWeight: 700, fontSize: 12 }}>{s.label}</span>
                      </div>
                      {run && run.timestamp && <div className="text-xs text-secondary" style={{ marginTop: 4 }}>{timeAgo(run.timestamp)}{run.details ? " — " + run.details : ""}</div>}
                    </div>
                  ))}
                </div>
                {h.cron_runs.length > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary className="text-xs text-secondary" style={{ cursor: "pointer" }}>All cron runs ({h.cron_runs.length})</summary>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 12 }}>
                      <thead><tr style={{ background: "#f8f9fa" }}><th style={{ padding: "4px 8px", textAlign: "left" }}>Task</th><th style={{ padding: "4px 8px", textAlign: "left" }}>Status</th><th style={{ padding: "4px 8px", textAlign: "left" }}>Details</th><th style={{ padding: "4px 8px", textAlign: "right" }}>Time</th></tr></thead>
                      <tbody>{h.cron_runs.map((r, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "4px 8px" }}>{r.task}</td>
                          <td style={{ padding: "4px 8px", color: r.status === "failed" ? "#dc2626" : "#16a34a" }}>{r.status === "completed" ? "✓" : "✗"} {r.status}</td>
                          <td style={{ padding: "4px 8px" }} className="text-secondary">{r.details || "—"}</td>
                          <td style={{ padding: "4px 8px", textAlign: "right" }} className="text-secondary">{timeAgo(r.timestamp)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </details>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pipeline Progress */}
      {pipeline && (() => {
        const openRate = pipeline.total_emails_sent > 0 ? ((pipeline.total_emails_opened / pipeline.total_emails_sent) * 100).toFixed(1) : "0";
        const replyStr = (pipeline.reply_breakdown || []).map(r => r.status.replace("replied_", "") + ": " + r.c).join(", ");
        const stages = [
          { label: "Queued", count: pipeline.queued || 0, detail: (pipeline.queued || 0) + " ready for generation", color: "#6b7280" },
          { label: "Generated", count: pipeline.generated || 0, detail: "Awaiting launch", color: "#3b82f6" },
          { label: "Sending", count: pipeline.sending || 0, detail: "Active sequences", color: "#14b8a6" },
          { label: "Sent", count: pipeline.total_emails_sent || 0, detail: "Emails delivered", color: "#0ea5e9" },
          { label: "Opened", count: pipeline.total_emails_opened || 0, detail: pipeline.total_emails_sent > 0 ? openRate + "% open rate" : "No sends yet", color: "#22c55e" },
          { label: "Replied", count: pipeline.replied || 0, detail: replyStr || "No replies yet", color: "#f59e0b" },
          { label: "Meetings", count: pipeline.meetings || 0, detail: "Booked", color: "#8b5cf6" },
        ];
        return (
          <div className="card mb-6">
            <div className="card-body">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
                <div className="font-semibold" style={{ fontSize: 16 }}>Pipeline Progress</div>
                <div className="text-sm text-secondary">Daily capacity: {pipeline.daily_launch_limit}/day</div>
              </div>
              <div style={{ display: "flex", alignItems: "stretch", gap: 0 }}>
                {stages.map((s, i) => (
                  <div key={s.label} style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <div style={{ flex: 1, padding: "12px 14px", borderLeft: "3px solid " + s.color, minWidth: 0 }}>
                      <div className="text-xs font-semibold text-secondary" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
                      <div className="font-bold" style={{ fontSize: 24, lineHeight: 1.2, marginTop: 4 }}>{s.count.toLocaleString()}</div>
                      <div className="text-xs text-secondary" style={{ marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.detail}</div>
                    </div>
                    {i < stages.length - 1 && <div className="text-secondary" style={{ fontSize: 18, padding: "0 4px", flexShrink: 0 }}>&gt;</div>}
                  </div>
                ))}
              </div>
              <div className="text-xs text-secondary" style={{ marginTop: 12, paddingTop: 8, borderTop: "1px solid var(--border, #e5e7eb)" }}>
                Bounced: {pipeline.bounced || 0} &nbsp;|&nbsp; Review: {pipeline.review || 0} &nbsp;|&nbsp; Not Ready: {pipeline.not_ready || 0} &nbsp;|&nbsp; Handed Off: {pipeline.handed_off || 0}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon navy"><Users size={18} /></div>
          <div className="stat-label">Total Prospects</div>
          <div className="stat-value">{stats.total_prospects?.toLocaleString()}</div>
          <div className="stat-sub">{stats.total_accounts?.toLocaleString()} accounts</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue"><Zap size={18} /></div>
          <div className="stat-label">Generated</div>
          <div className="stat-value">{stats.generated || 0}</div>
          <div className="stat-sub">{stats.active_sequences || 0} in sequence</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon amber"><Mail size={18} /></div>
          <div className="stat-label">Open Rate</div>
          <div className="stat-value">{openRate}%</div>
          <div className="stat-sub">{emailStats.sent || 0} sent, {emailStats.replied || 0} replied</div>
        </div>
        {(() => {
          const sl = stats.serious_leads;
          const pct = sl ? (sl.percentage || 0) : 0;
          const color = pct >= 1.0 ? "#16a34a" : pct >= 0.3 ? "#eab308" : "#dc2626";
          const tooltip = sl
            ? `INTERESTED: ${sl.breakdown?.interested || 0} · QUESTION: ${sl.breakdown?.question || 0} · Meetings: ${sl.breakdown?.meetings || 0}`
            : "Loading...";
          return (
            <div className="stat-card" title={tooltip}>
              <div className="stat-icon green"><Target size={18} /></div>
              <div className="stat-label">Serious Lead %</div>
              <div className="stat-value" style={{ color }}>{sl ? pct.toFixed(2) : "—"}%</div>
              <div className="stat-sub">{sl ? `${sl.serious_count} / ${sl.emails_sent} emails` : "Loading..."}</div>
            </div>
          );
        })()}
        <div className="stat-card">
          <div className="stat-icon green"><Calendar size={18} /></div>
          <div className="stat-label">Meetings</div>
          <div className="stat-value">{stats.meetings_scheduled || 0}</div>
          <div className="stat-sub">{stats.meetings_completed || 0} completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: autoPilot === "on" ? "var(--green-bg, #dcfce7)" : "var(--red-bg, #fee2e2)", color: autoPilot === "on" ? "#16a34a" : "#dc2626" }}><Zap size={18} /></div>
          <div className="stat-label">Auto-Pilot</div>
          <div className="stat-value" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", display: "inline-block", background: autoPilot === "on" ? "#16a34a" : "#dc2626" }} />
            {autoPilot === "on" ? "ON" : "OFF"}
          </div>
          <div className="stat-sub">{autoPilot === "on" ? "Running" : "Paused"}</div>
        </div>
      </div>

      {stats?.trend && stats.trend.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><h3 style={{ margin: 0 }}>Daily Activity (Last 30 Days)</h3></div>
          <div className="card-body" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tickFormatter={d => d.slice(5)} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sent" stroke="#1B3A4B" name="Sent" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="replied" stroke="#10B981" name="Replies" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card mb-6" style={{ marginTop: 16 }}>
        <div className="card-header"><h3>Quick Actions</h3></div>
        <div className="card-body">
          <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={async () => {
              if (!window.confirm("This will generate emails for all ready prospects. Continue?")) return;
              try {
                await API.post("/ai/batch-generate", { prospect_ids: [] });
                showToast("Batch generation started", "success");
              } catch (e) { showToast(e.response?.data?.error || "Failed to start batch generate", "error"); }
            }}><Brain size={14} /> Run Batch Generate</button>
            <button className="btn btn-secondary" onClick={async () => {
              try {
                await API.get("/digest/send");
                showToast("Digest sent", "success");
              } catch (e) { showToast(e.response?.data?.error || "Failed to send digest", "error"); }
            }}><Mail size={14} /> Send Digest Now</button>
            <button className="btn btn-secondary" onClick={async () => {
              try {
                await API.post("/health/domains/check");
                loadDomainHealth();
                showToast("Domain health check triggered", "success");
              } catch (e) { showToast(e.response?.data?.error || "Check failed", "error"); }
            }}><Shield size={14} /> Check Domain Health</button>
          </div>
        </div>
      </div>

      {(stats.tag_breakdown || []).length > 0 && (
        <div className="card mb-6">
          <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>Campaign Tags</h3>
            <span className="badge badge-green">{stats.ready_count || 0} ready to launch</span>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(stats.tag_breakdown || []).map(t => (
                <div key={t.campaign_tag} style={{ padding: "6px 12px", borderRadius: 6, background: TAG_COLORS[t.campaign_tag] || "#6B7280", color: "#fff", fontSize: 13, fontWeight: 600 }}>
                  {t.campaign_tag}: {t.count}
                </div>
              ))}
            </div>
            {(stats.by_ready || []).length > 0 && (
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                {(stats.by_ready || []).map(r => (
                  <span key={r.campaign_ready} className={`badge badge-${READY_BADGE[r.campaign_ready] || "gray"}`} style={{ fontSize: 11 }}>
                    {READY_LABELS[r.campaign_ready] || r.campaign_ready}: {r.count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid-2 mb-6">
        <div className="card">
          <div className="card-header"><h3>By Service Track</h3></div>
          <div className="card-body">
            {trackData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart><Pie data={trackData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                  {trackData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>No data yet</p></div>}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>By Status</h3></div>
          <div className="card-body">
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={statusData}><XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94A3B8' }} angle={-20} /><YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} /><Tooltip /><Bar dataKey="count" fill="#1B3A4B" radius={[4,4,0,0]} /></BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>No data yet</p></div>}
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header"><h3>Upcoming Meetings</h3></div>
          <div className="card-body" style={{ maxHeight: 280, overflowY: "auto" }}>
            {(stats.upcoming_meetings || []).length > 0 ? (
              <table className="data-table">
                <thead><tr><th>Prospect</th><th>Company</th><th>Date</th></tr></thead>
                <tbody>{stats.upcoming_meetings.map(m => (
                  <tr key={m.id}><td className="font-semibold">{m.first_name} {m.last_name}</td><td className="text-secondary">{m.company}</td><td className="text-sm text-tertiary">{new Date(m.scheduled_at).toLocaleDateString()}</td></tr>
                ))}</tbody>
              </table>
            ) : <div className="empty-state"><Calendar size={28} /><p>No upcoming meetings</p></div>}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Recent Activity</h3></div>
          <div className="card-body" style={{ maxHeight: 280, overflowY: "auto" }}>
            {(stats.recent_activities || []).map(a => (
              <div key={a.id} className="timeline-item">
                <div className={`timeline-dot ${a.direction === 'inbound' ? 'inbound' : 'outbound'}`} />
                <div style={{ flex: 1 }}>
                  <span className="font-semibold text-sm">{a.first_name} {a.last_name}</span>
                  <span className="text-tertiary text-sm"> at {a.company}</span>
                  <div className="timeline-meta">{a.type?.replace(/_/g, " ")} via {a.channel} &middot; {new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
            {(stats.recent_activities || []).length === 0 && <div className="empty-state"><Clock size={28} /><p>No activity yet</p></div>}
          </div>
        </div>
      </div>

      {/* Batch Jobs */}
      {(stats.batch_jobs || []).length > 0 && (
        <div className="card mt-6">
          <div className="card-header"><h3>Recent Jobs</h3></div>
          <div className="card-body">
            {stats.batch_jobs.map(j => (
              <div key={j.id} className="flex items-center gap-4 mb-3">
                <span className={`badge badge-${j.status === 'completed' ? 'green' : j.status === 'running' ? 'blue' : j.status === 'failed' ? 'red' : 'gray'}`}>{j.status}</span>
                <span className="text-sm font-medium">{j.type?.replace(/_/g, " ")}</span>
                {j.total_items > 0 && (
                  <div style={{ flex: 1, maxWidth: 200 }}>
                    <div className="progress-bar"><div className={`progress-bar-fill ${j.status === 'completed' ? 'green' : ''}`} style={{ width: `${Math.min(100, ((j.processed_items || 0) / j.total_items) * 100)}%` }} /></div>
                  </div>
                )}
                <span className="text-xs text-tertiary">{j.processed_items || 0} / {j.total_items || 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain Health */}
      <div className="card mt-6">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3><Shield size={16} style={{ display: "inline", marginRight: 6, verticalAlign: "text-bottom" }} />Domain Health</h3>
          <button className="btn btn-secondary btn-sm" disabled={dhChecking} onClick={async () => {
            setDhChecking(true);
            try {
              await API.post("/health/domains/check");
              loadDomainHealth();
              showToast("Domain health check complete", "success");
            } catch (e) { showToast(e.response?.data?.error || "Check failed", "error"); }
            setDhChecking(false);
          }}><RefreshCw size={14} className={dhChecking ? "spin" : ""} /> {dhChecking ? "Checking..." : "Check Now"}</button>
        </div>
        <div className="card-body">
          {domainHealth.length === 0 ? (
            <div className="empty-state"><Globe size={28} /><p>No health data yet. Click "Check Now" to run first scan.</p></div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
              {domainHealth.map(d => (
                <div key={d.domain} style={{
                  border: d.blacklisted ? "2px solid #dc2626" : "1px solid var(--border-color, #e5e7eb)",
                  borderRadius: 10, padding: 16, background: "var(--card-bg, #fff)",
                  position: "relative"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span className="font-semibold text-sm">{d.domain}</span>
                    <span style={{
                      display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 13, fontWeight: 600,
                      background: d.score >= 75 ? "#dcfce7" : d.score >= 50 ? "#fef9c3" : "#fee2e2",
                      color: d.score >= 75 ? "#166534" : d.score >= 50 ? "#854d0e" : "#991b1b"
                    }}>{d.score}/100</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", fontSize: 13 }}>
                    <div>{d.mx ? <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: "text-bottom" }} /> : <XCircle size={14} color="#dc2626" style={{ verticalAlign: "text-bottom" }} />} MX</div>
                    <div>{d.spf ? <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: "text-bottom" }} /> : <XCircle size={14} color="#dc2626" style={{ verticalAlign: "text-bottom" }} />} SPF</div>
                    <div>{d.dkim ? <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: "text-bottom" }} /> : <XCircle size={14} color="#dc2626" style={{ verticalAlign: "text-bottom" }} />} DKIM</div>
                    <div>{d.dmarc ? <CheckCircle size={14} color="#16a34a" style={{ verticalAlign: "text-bottom" }} /> : <XCircle size={14} color="#dc2626" style={{ verticalAlign: "text-bottom" }} />} DMARC{d.dmarc ? ` (${d.dmarc})` : ""}</div>
                  </div>
                  {d.blacklisted ? (
                    <div style={{ marginTop: 10, padding: "6px 10px", background: "#fee2e2", borderRadius: 6, fontSize: 12, color: "#991b1b" }}>
                      <AlertTriangle size={12} style={{ verticalAlign: "text-bottom", marginRight: 4 }} />
                      Blacklisted: {(() => { try { return JSON.parse(d.blacklists).join(", "); } catch { return d.blacklists; } })()}
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#16a34a" }}>
                      <CheckCircle size={12} style={{ verticalAlign: "text-bottom", marginRight: 4 }} />Not blacklisted
                    </div>
                  )}
                  <div className="text-xs text-tertiary" style={{ marginTop: 8 }}>
                    Checked: {d.checked_at ? new Date(d.checked_at).toLocaleString() : "Never"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* LinkedIn Signals */}
      <div className="card mt-6">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3><UserCheck size={16} style={{ display: "inline", marginRight: 6, verticalAlign: "text-bottom" }} />LinkedIn Signals</h3>
          <button className="btn btn-secondary btn-sm" disabled={liMonitoring} onClick={async () => {
            setLiMonitoring(true);
            try {
              const { data } = await API.post("/linkedin/monitor", { limit: 50 });
              showToast(`Checked ${data.checked} profiles, ${data.changes_detected} changes found`, "success");
              const r = await API.get("/linkedin/changes");
              const now = Date.now();
              const week = 7 * 24 * 60 * 60 * 1000;
              setLiChanges((r.data.changes || []).filter(c => (now - new Date(c.detected_at).getTime()) < week));
            } catch (e) { showToast(e.response?.data?.error || "Monitor failed", "error"); }
            setLiMonitoring(false);
          }}><RefreshCw size={14} className={liMonitoring ? "spin" : ""} /> {liMonitoring ? "Monitoring..." : "Run Monitor"}</button>
        </div>
        <div className="card-body">
          {liChanges.length === 0 ? (
            <div className="empty-state"><UserCheck size={28} /><p>No recent LinkedIn changes detected</p></div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table" style={{ width: "100%", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ padding: "8px 12px" }}>Prospect</th>
                    <th style={{ padding: "8px 12px" }}>Change</th>
                    <th style={{ padding: "8px 12px" }}>Old</th>
                    <th style={{ padding: "8px 12px" }}>New</th>
                    <th style={{ padding: "8px 12px" }}>Detected</th>
                    <th style={{ padding: "8px 12px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {liChanges.map(c => (
                    <tr key={c.id} style={{ opacity: c.actioned ? 0.5 : 1 }}>
                      <td style={{ padding: "8px 12px" }}>{c.first_name} {c.last_name}{c.company ? ` (${c.company})` : ""}</td>
                      <td style={{ padding: "8px 12px" }}><span className={`status-badge status-${c.change_type === 'company_change' ? 'amber' : 'blue'}`}>{c.change_type.replace("_", " ")}</span></td>
                      <td style={{ padding: "8px 12px", color: "#991b1b" }}>{c.old_value}</td>
                      <td style={{ padding: "8px 12px", color: "#166534" }}>{c.new_value}</td>
                      <td style={{ padding: "8px 12px" }} className="text-tertiary">{new Date(c.detected_at).toLocaleDateString()}</td>
                      <td style={{ padding: "8px 12px" }}>
                        {!c.actioned && (
                          <div style={{ display: "flex", gap: 4 }}>
                            <button className="btn btn-secondary btn-xs" onClick={async () => {
                              try {
                                await API.post(`/linkedin/changes/${c.id}/action`, { action: "reassess" });
                                setLiChanges(prev => prev.map(x => x.id === c.id ? { ...x, actioned: 1 } : x));
                                showToast("Re-assessed", "success");
                              } catch { showToast("Failed", "error"); }
                            }}>Re-assess</button>
                            <button className="btn btn-primary btn-xs" onClick={async () => {
                              try {
                                await API.post(`/linkedin/changes/${c.id}/action`, { action: "reengage" });
                                setLiChanges(prev => prev.map(x => x.id === c.id ? { ...x, actioned: 1 } : x));
                                showToast("Re-engaged", "success");
                              } catch { showToast("Failed", "error"); }
                            }}>Re-engage</button>
                            <button className="btn btn-secondary btn-xs" onClick={async () => {
                              try {
                                await API.post(`/linkedin/changes/${c.id}/action`, { action: "dismiss" });
                                setLiChanges(prev => prev.map(x => x.id === c.id ? { ...x, actioned: 1 } : x));
                              } catch { showToast("Failed", "error"); }
                            }}>Dismiss</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ PROSPECTS ============
function Prospects() {
  const [data, setData] = useState({ prospects: [], total: 0, pages: 0 });
  const [filters, setFilters] = useState({ page: 1, track: "", status: "", geo: "", search: "", campaign_tag: "", campaign_ready: "" });
  const navigate = useNavigate();
  const [showToast, toastEl] = useToast();
  const [selected, setSelected] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [campaignList, setCampaignList] = useState([]);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ first_name: "", last_name: "", email: "", company: "", title: "", service_track: "C", geo_tier: "1", location_country: "", linkedin_url: "" });
  const [addSaving, setAddSaving] = useState(false);
  const [dbTags, setDbTags] = useState([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showReadyDropdown, setShowReadyDropdown] = useState(false);
  const [customTagInput, setCustomTagInput] = useState("");

  const load = useCallback(async () => {
    const params = { ...filters, limit: 30 };
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
    try {
      const { data: d } = await API.get("/prospects", { params });
      setData(d);
    } catch {}
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { API.get("/prospects/campaign-tags").then(r => setDbTags(r.data || [])).catch(() => {}); }, []);

  const bulkTag = async (tag) => {
    setShowTagDropdown(false); setActionLoading(true);
    try {
      await API.post("/prospects/bulk-tag", { ids: [...selected], campaign_tag: tag });
      showToast(`Tagged ${selected.size} prospects as "${tag}"`, "success");
      setSelected(new Set()); load();
      API.get("/prospects/campaign-tags").then(r => setDbTags(r.data || [])).catch(() => {});
    } catch (err) { showToast(err.response?.data?.error || "Tag failed", "error"); }
    finally { setActionLoading(false); }
  };
  const bulkReady = async (readyVal) => {
    setShowReadyDropdown(false); setActionLoading(true);
    try {
      await API.post("/prospects/bulk-tag", { ids: [...selected], campaign_ready: readyVal });
      showToast(`Set ${selected.size} prospects to "${readyVal}"`, "success");
      setSelected(new Set()); load();
    } catch (err) { showToast(err.response?.data?.error || "Failed", "error"); }
    finally { setActionLoading(false); }
  };

  const toggleSelect = (id, e) => { e.stopPropagation(); setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleAll = () => { if (selected.size === data.prospects.length) setSelected(new Set()); else setSelected(new Set(data.prospects.map(p => p.id))); };
  const initiateContact = async () => {
    if (selected.size === 0) return;
    setActionLoading(true);
    try {
      const { data: r } = await API.post("/prospects/initiate", { prospect_ids: [...selected] });
      if (r.launched > 0) showToast(`Launched ${r.launched} campaigns`, "success");
      else showToast(r.message || "No prospects launched", "error");
      setSelected(new Set()); load();
    } catch (err) { showToast(err.response?.data?.error || "Failed", "error"); }
    finally { setActionLoading(false); }
  };
  const addToCampaign = async (campaignId) => {
    setShowCampaignDropdown(false); setActionLoading(true);
    try {
      const { data: r } = await API.post(`/campaigns/${campaignId}/add-prospects`, { prospect_ids: [...selected] });
      if (r.added > 0) showToast(`Added ${r.added} prospects to campaign`, "success");
      else showToast(r.message || "No prospects added", "error");
      setSelected(new Set()); load();
    } catch (err) { showToast(err.response?.data?.error || "Failed", "error"); }
    finally { setActionLoading(false); }
  };
  const loadCampaigns = () => { API.get("/smartlead/campaigns").then(r => setCampaignList(Array.isArray(r.data) ? r.data : [])).catch(() => {}); setShowCampaignDropdown(v => !v); };

  const saveProspect = async () => {
    if (!addForm.first_name.trim() || !addForm.email.trim()) return showToast("First name and email required", "error");
    setAddSaving(true);
    try {
      await API.post("/prospects/add", addForm);
      showToast(`Added ${addForm.first_name} ${addForm.last_name}`, "success");
      setAddForm({ first_name: "", last_name: "", email: "", company: "", title: "", service_track: "C", geo_tier: "1", location_country: "", linkedin_url: "" });
      setShowAddForm(false); load();
    } catch (err) { showToast(err.response?.data?.error || "Failed to add", "error"); }
    finally { setAddSaving(false); }
  };

  const exportCSV = async () => {
    try {
      const resp = await API.get("/prospects/export/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a"); a.href = url; a.download = "prospects_export.csv"; a.click();
      showToast("Exported", "success");
    } catch {
      // Fallback: export from current data
      const rows = [["Name","Company","Title","Email","Track","Geo","Score","Status"]];
      data.prospects.forEach(p => rows.push([`${p.first_name} ${p.last_name}`,p.company,p.title,p.email,p.service_track,p.geo_tier,p.lead_score,p.status]));
      const csv = rows.map(r => r.map(c => `"${(c||'').toString().replace(/"/g,'""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "prospects_export.csv"; a.click();
      showToast("Exported (from page data)", "info");
    }
  };

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div>
          <h2>Prospects</h2>
          <div className="page-subtitle">{data.total?.toLocaleString()} total in pipeline</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(v => !v)}><Plus size={14} /> Add Prospect</button>
          <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={14} /> Export</button>
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
        </div>
      </div>

      {showAddForm && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-header"><h3>Add Prospect</h3></div>
          <div className="card-body">
            <div className="grid-2" style={{ marginBottom: 10 }}>
              <div className="form-group"><label className="form-label">First Name *</label><input className="form-input" value={addForm.first_name} onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={addForm.last_name} onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))} /></div>
            </div>
            <div className="grid-2" style={{ marginBottom: 10 }}>
              <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Company</label><input className="form-input" value={addForm.company} onChange={e => setAddForm(f => ({ ...f, company: e.target.value }))} /></div>
            </div>
            <div className="grid-2" style={{ marginBottom: 10 }}>
              <div className="form-group"><label className="form-label">Title</label><input className="form-input" value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Country</label><input className="form-input" value={addForm.location_country} onChange={e => setAddForm(f => ({ ...f, location_country: e.target.value }))} /></div>
            </div>
            <div className="grid-2" style={{ marginBottom: 10 }}>
              <div className="form-group"><label className="form-label">Service Track</label><select className="form-input" value={addForm.service_track} onChange={e => setAddForm(f => ({ ...f, service_track: e.target.value }))}><option value="C">C - Consultancy</option><option value="I">I - Investments</option><option value="A">A - Accounting</option><option value="T">T - Transaction Advisory</option><option value="F">F - Family Office</option><option value="E">E - Execution</option></select></div>
              <div className="form-group"><label className="form-label">Geography</label><select className="form-input" value={addForm.geo_tier} onChange={e => setAddForm(f => ({ ...f, geo_tier: e.target.value }))}><option value="1">Tier 1 - UAE</option><option value="2">Tier 2 - GCC</option><option value="3">Tier 3 - Lebanon</option><option value="4">Tier 4 - Africa</option></select></div>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}><label className="form-label">LinkedIn URL</label><input className="form-input" value={addForm.linkedin_url} onChange={e => setAddForm(f => ({ ...f, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/in/..." /></div>
            <div className="flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={saveProspect} disabled={addSaving}>{addSaving ? "Saving..." : "Save Prospect"}</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <input className="form-input" style={{ width: 240 }} placeholder="Search prospects..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        <select className="form-select" style={{ width: 170 }} value={filters.track} onChange={e => setFilters(f => ({ ...f, track: e.target.value, page: 1 }))}>
          <option value="">All Tracks</option>
          {Object.entries(TRACK_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select" style={{ width: 130 }} value={filters.geo} onChange={e => setFilters(f => ({ ...f, geo: e.target.value, page: 1 }))}>
          <option value="">All Geos</option>
          {Object.entries(GEO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select" style={{ width: 170 }} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Statuses</option>
          {["new","imported","generated","enriched","sequence_active","engaged","replied_interested","replied_question","meeting_scheduled","won","lost","nurture"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select className="form-select" style={{ width: 170 }} value={filters.campaign_tag} onChange={e => setFilters(f => ({ ...f, campaign_tag: e.target.value, page: 1 }))}>
          <option value="">All Tags</option>
          {[...new Set([...DEFAULT_TAGS, ...dbTags])].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-select" style={{ width: 140 }} value={filters.campaign_ready} onChange={e => setFilters(f => ({ ...f, campaign_ready: e.target.value, page: 1 }))}>
          <option value="">All Ready</option>
          {Object.entries(READY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th style={{ width: 32 }}><input type="checkbox" checked={data.prospects.length > 0 && selected.size === data.prospects.length} onChange={toggleAll} /></th><th>Name</th><th>Company</th><th>Title</th><th>Track</th><th>Tag</th><th>Ready</th><th>Geo</th><th>Score</th><th>Status</th><th>Spam</th><th>Email Status</th><th></th></tr></thead>
          <tbody>
            {data.prospects.map(p => (
              <tr key={p.id} className="clickable" style={{ background: selected.has(p.id) ? "var(--blue-bg, #eff6ff)" : undefined }}>
                <td onClick={e => e.stopPropagation()}><input type="checkbox" checked={selected.has(p.id)} onChange={e => toggleSelect(p.id, e)} /></td>
                <td className="font-semibold text-primary" onClick={() => navigate(`/prospects/${p.id}`)}>{p.first_name} {p.last_name}</td>
                <td className="text-secondary" onClick={() => navigate(`/prospects/${p.id}`)}>{p.company}</td>
                <td className="text-sm text-tertiary truncate" style={{ maxWidth: 180 }} onClick={() => navigate(`/prospects/${p.id}`)}>{p.title}</td>
                <td><span className="badge badge-navy">{p.service_track}</span></td>
                <td>{p.campaign_tag && <span className="badge" style={{ fontSize: 10, background: TAG_COLORS[p.campaign_tag] || "#6B7280", color: "#fff" }}>{p.campaign_tag}</span>}</td>
                <td><span className={`badge badge-${READY_BADGE[p.campaign_ready] || "gray"}`} style={{ fontSize: 10 }}>{READY_LABELS[p.campaign_ready] || p.campaign_ready || "pending"}</span></td>
                <td className="text-sm">{GEO_LABELS[p.geo_tier] || p.geo_tier}</td>
                <td><span className={`badge ${p.lead_score >= 80 ? "badge-green" : p.lead_score >= 60 ? "badge-blue" : p.lead_score >= 30 ? "badge-amber" : "badge-gray"}`}>{p.lead_score}</span></td>
                <td><span className={`badge badge-${STATUS_BADGE[p.status] || "gray"}`}>{p.status?.replace(/_/g, " ")}</span></td>
                <td>{p.spam_score != null && <span className={`badge ${p.spam_score <= 10 ? "badge-green" : p.spam_score < 25 ? "badge-amber" : "badge-red"}`} style={{ fontSize: 10 }}>{p.spam_score <= 10 ? "Safe" : p.spam_score < 25 ? "Warn" : "Spam risk"}</span>}</td>
                <td>{p.email_quality === "good" ? <span className="badge badge-green" style={{ fontSize: 10 }}>Verified</span> : p.email_quality === "catch_all" ? <span className="badge badge-amber" style={{ fontSize: 10 }}>Catch-all</span> : p.email_quality === "unknown" ? <span className="badge badge-amber" style={{ fontSize: 10 }}>Unknown</span> : p.email_quality === "bad" ? <span className="badge badge-red" style={{ fontSize: 10 }}>Invalid</span> : p.email_quality === "disposable" ? <span className="badge badge-red" style={{ fontSize: 10 }}>Disposable</span> : <span className="badge badge-gray" style={{ fontSize: 10 }}>Unverified</span>}</td>
                <td><ChevronRight size={14} className="text-tertiary" /></td>
              </tr>
            ))}
            {data.prospects.length === 0 && <tr><td colSpan={13}><div className="empty-state"><Users size={28} /><h3>No prospects found</h3><p>Import data to get started</p></div></td></tr>}
          </tbody>
        </table>
        {data.pages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Previous</button>
            <span className="page-info">Page {filters.page} of {data.pages}</span>
            <button className="btn btn-secondary btn-sm" disabled={filters.page >= data.pages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next</button>
          </div>
        )}
      </div>

      {selected.size > 0 && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--bg-card, #fff)", borderTop: "2px solid var(--blue, #2563eb)", padding: "10px 24px", display: "flex", alignItems: "center", gap: 12, zIndex: 1000, boxShadow: "0 -2px 8px rgba(0,0,0,0.1)" }}>
          <span className="font-semibold">{selected.size} selected</span>
          <button className="btn btn-primary btn-sm" onClick={initiateContact} disabled={actionLoading}>{actionLoading ? "Launching..." : <><Zap size={14} /> Initiate Contact ({selected.size})</>}</button>
          <div style={{ position: "relative" }}>
            <button className="btn btn-secondary btn-sm" onClick={loadCampaigns} disabled={actionLoading}><Plus size={14} /> Add to Campaign</button>
            {showCampaignDropdown && (
              <div style={{ position: "absolute", bottom: "100%", left: 0, background: "var(--bg-card, #fff)", border: "1px solid var(--border)", borderRadius: "var(--radius, 6px)", boxShadow: "0 -4px 12px rgba(0,0,0,0.1)", minWidth: 250, maxHeight: 200, overflowY: "auto", marginBottom: 4 }}>
                {campaignList.length === 0 ? <div style={{ padding: 12, fontSize: 13 }} className="text-tertiary">No active campaigns</div> : campaignList.map(c => (
                  <div key={c.id} className="clickable" style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)" }} onClick={() => addToCampaign(c.id)}>
                    {c.name} <span className={`badge badge-${String(c.status).toUpperCase() === "ACTIVE" ? "green" : "amber"}`} style={{ fontSize: 10 }}>{c.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowTagDropdown(v => !v)} disabled={actionLoading}><Target size={14} /> Set Tag</button>
            {showTagDropdown && (
              <div style={{ position: "absolute", bottom: "100%", left: 0, background: "var(--bg-card, #fff)", border: "1px solid var(--border)", borderRadius: "var(--radius, 6px)", boxShadow: "0 -4px 12px rgba(0,0,0,0.1)", minWidth: 220, maxHeight: 320, overflowY: "auto", marginBottom: 4, zIndex: 1001 }}>
                {DEFAULT_TAGS.map(t => (
                  <div key={t} className="clickable" style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)" }} onClick={() => bulkTag(t)}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: TAG_COLORS[t] || "#6B7280", marginRight: 8 }} />{t}
                  </div>
                ))}
                {dbTags.filter(t => !DEFAULT_TAGS.includes(t)).map(t => (
                  <div key={t} className="clickable" style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)" }} onClick={() => bulkTag(t)}>
                    {t}
                  </div>
                ))}
                <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
                  <input className="form-input" style={{ fontSize: 12, padding: "4px 8px", marginBottom: 4 }} placeholder="Custom tag..." value={customTagInput} onChange={e => setCustomTagInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && customTagInput.trim()) { bulkTag(customTagInput.trim()); setCustomTagInput(""); }}} />
                  {customTagInput.trim() && <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => { bulkTag(customTagInput.trim()); setCustomTagInput(""); }}>Apply</button>}
                </div>
                <div className="clickable" style={{ padding: "8px 12px", fontSize: 13, color: "#dc2626" }} onClick={() => bulkTag(null)}>Clear Tag</div>
              </div>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowReadyDropdown(v => !v)} disabled={actionLoading}><CheckCircle size={14} /> Set Ready</button>
            {showReadyDropdown && (
              <div style={{ position: "absolute", bottom: "100%", left: 0, background: "var(--bg-card, #fff)", border: "1px solid var(--border)", borderRadius: "var(--radius, 6px)", boxShadow: "0 -4px 12px rgba(0,0,0,0.1)", minWidth: 180, marginBottom: 4, zIndex: 1001 }}>
                {Object.entries(READY_LABELS).map(([k, v]) => (
                  <div key={k} className="clickable" style={{ padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)" }} onClick={() => bulkReady(k)}>
                    <span className={`badge badge-${READY_BADGE[k]}`} style={{ fontSize: 10, marginRight: 8 }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={async () => {
            setActionLoading(true);
            try {
              const { data: r } = await API.post("/prospects/batch-assess", { prospect_ids: [...selected] });
              showToast(`Assessed ${r.assessed} of ${r.total} prospects`, "success");
              setSelected(new Set()); load();
            } catch (err) { showToast(err.response?.data?.error || "Assessment failed", "error"); }
            finally { setActionLoading(false); }
          }} disabled={actionLoading}><Target size={14} /> Assess Selected</button>
          <button className="btn btn-secondary btn-sm" onClick={async () => {
            setActionLoading(true);
            try {
              const { data: r } = await API.post("/prospects/verify", { prospect_ids: [...selected] });
              showToast(`Verified ${r.verified} email${r.verified !== 1 ? "s" : ""}`, "success");
              setSelected(new Set()); load();
            } catch (err) { showToast(err.response?.data?.error || "Verification failed", "error"); }
            finally { setActionLoading(false); }
          }} disabled={actionLoading}><Shield size={14} /> Verify Selected</button>
          <button className="btn btn-secondary btn-sm" onClick={async () => {
            setActionLoading(true);
            try {
              const { data: r } = await API.post("/brightdata/enrich", { prospect_ids: [...selected] });
              showToast(r.message || `LinkedIn enrichment started (${r.total} prospects)`, "success");
            } catch (err) { showToast(err.response?.data?.error || "Enrichment failed", "error"); }
            finally { setActionLoading(false); }
          }} disabled={actionLoading}><Globe size={14} /> Enrich LinkedIn</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}
    </div>
  );
}

// ============ PROSPECT DETAIL ============
function ProspectDetail() {
  const id = window.location.pathname.split("/").pop();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("info");
  const navigate = useNavigate();
  const [showToast, toastEl] = useToast();

  useEffect(() => {
    API.get(`/prospects/${id}`).then(r => setData(r.data)).catch(() => navigate("/prospects"));
  }, [id, navigate]);

  if (!data) return <div className="main-content"><p className="text-tertiary">Loading...</p></div>;
  const { prospect: p, activities, meetings, deals } = data;

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div>
          <div className="text-xs text-tertiary font-medium" style={{ cursor: "pointer", marginBottom: 8 }} onClick={() => navigate("/prospects")}>&larr; Back to Prospects</div>
          <h2>{p.first_name} {p.last_name}</h2>
          <div className="text-sm text-secondary">{p.title} at {p.company}</div>
        </div>
        <div className="flex gap-2 items-center">
          <span className={`badge badge-${STATUS_BADGE[p.status] || "gray"}`}>{p.status?.replace(/_/g, " ")}</span>
          <span className={`badge ${p.lead_score >= 80 ? "badge-green" : p.lead_score >= 60 ? "badge-blue" : "badge-amber"}`}>Score: {p.lead_score}</span>
          {p.spam_score != null && <span className={`badge ${p.spam_score <= 10 ? "badge-green" : p.spam_score < 25 ? "badge-amber" : "badge-red"}`}>{p.spam_score <= 10 ? "Safe" : p.spam_score < 25 ? "Warn" : "Spam risk"} ({p.spam_score})</span>}
          {p.email_quality ? <span className={`badge ${p.email_quality === "good" ? "badge-green" : p.email_quality === "catch_all" || p.email_quality === "unknown" ? "badge-amber" : "badge-red"}`}>{p.email_quality === "good" ? "Verified" : p.email_quality === "catch_all" ? "Catch-all" : p.email_quality === "unknown" ? "Unknown" : p.email_quality === "bad" ? "Invalid" : p.email_quality === "disposable" ? "Disposable" : p.email_quality}</span> : null}
          {p.email_verified_at && <span className="text-xs text-tertiary">Verified: {new Date(p.email_verified_at).toLocaleDateString()}</span>}
        </div>
      </div>

      {p.status === "handed_off" && (
        <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "12px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={16} style={{ color: "#d97706" }} />
          <span className="text-sm font-semibold" style={{ color: "#92400e" }}>This lead has been handed off. The sales engine has stopped all automated actions.</span>
        </div>
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn-secondary btn-sm" onClick={async () => {
          try {
            const { data: r } = await API.get(`/prospects/${id}/verify`);
            showToast(`Email ${r.quality === "good" ? "verified" : "quality: " + r.quality}`, r.quality === "good" ? "success" : "error");
            API.get(`/prospects/${id}`).then(r => setData(r.data));
          } catch (err) { showToast(err.response?.data?.error || "Verification failed", "error"); }
        }}><Shield size={14} /> Verify Email</button>
        <button className="btn btn-secondary btn-sm" onClick={async () => {
          try {
            showToast("Enriching LinkedIn...", "info");
            const { data: r } = await API.post("/brightdata/enrich", { prospect_ids: [Number(id)] });
            showToast(r.message || `Enrichment started (job ${r.job_id})`, "success");
          } catch (err) { showToast(err.response?.data?.error || "Enrichment failed", "error"); }
        }}><Globe size={14} /> Enrich LinkedIn</button>
        <span style={{ borderLeft: "1px solid var(--border)", height: 20, margin: "0 4px" }} />
        <span className="text-xs text-tertiary">Primary:</span>
        <select className="form-select" style={{ width: 130, fontSize: 12, padding: "4px 8px" }} value={p.service_track || "C"} onChange={async (e) => {
          const newTrack = e.target.value;
          let allTracks = [newTrack];
          try { const st = JSON.parse(p.service_tracks || "[]"); allTracks = [newTrack, ...st.filter(t => t !== newTrack && t !== p.service_track)]; } catch {}
          try {
            await API.put(`/prospects/${id}`, { service_track: newTrack });
            await API.post("/prospects/bulk-tracks", { ids: [Number(id)], service_track: newTrack, service_tracks: allTracks });
            setData(prev => ({ ...prev, prospect: { ...prev.prospect, service_track: newTrack, service_tracks: JSON.stringify(allTracks) } }));
            showToast("Track updated", "success");
          } catch (err) { showToast("Update failed", "error"); }
        }}>
          {Object.entries(TRACK_LABELS).map(([k, v]) => <option key={k} value={k}>{k} - {v}</option>)}
        </select>
        <span className="text-xs text-tertiary">Secondary:</span>
        {Object.keys(TRACK_LABELS).filter(t => t !== p.service_track).map(t => {
          const currentTracks = (() => { try { return JSON.parse(p.service_tracks || "[]"); } catch { return []; } })();
          const isChecked = currentTracks.includes(t);
          return (
            <label key={t} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={isChecked} onChange={async () => {
                const newTracks = isChecked ? currentTracks.filter(x => x !== t) : [...currentTracks, t];
                if (!newTracks.includes(p.service_track)) newTracks.unshift(p.service_track);
                try {
                  await API.post("/prospects/bulk-tracks", { ids: [Number(id)], service_track: p.service_track, service_tracks: newTracks });
                  setData(prev => ({ ...prev, prospect: { ...prev.prospect, service_tracks: JSON.stringify(newTracks) } }));
                } catch {}
              }} />
              <span style={{ color: TRACK_COLORS[t] || "#888" }}>{t}</span>
            </label>
          );
        })}
      </div>

      <div className="tab-bar">
        {["info","emails","linkedin","activity"].map(t => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>{t === "info" ? "Contact Info" : t === "emails" ? "Emails" : t === "linkedin" ? "LinkedIn" : "Activity"}</div>
        ))}
      </div>

      {tab === "info" && (
        <div className="grid-2">
          <div className="card">
            <div className="card-header"><h3>Contact Details</h3></div>
            <div className="card-body">
              <div className="info-row"><div className="info-label">Email</div><div className="info-value flex items-center gap-2">{p.email || "N/A"} {p.email && <Copy size={12} className="text-tertiary" style={{ cursor: "pointer" }} onClick={() => copyToClipboard(p.email, showToast)} />}</div></div>
              <div className="info-row"><div className="info-label">Phone</div><div className="info-value">{p.phone || "N/A"}</div></div>
              <div className="info-row"><div className="info-label">Location</div><div className="info-value">{[p.location_city, p.location_country].filter(Boolean).join(", ") || "N/A"}</div></div>
              <div className="info-row"><div className="info-label">Industry</div><div className="info-value">{p.industry || "N/A"}</div></div>
              <div className="info-row"><div className="info-label">Company Size</div><div className="info-value">{p.company_size || "N/A"}</div></div>
              <div className="info-row"><div className="info-label">Seniority</div><div className="info-value">{p.seniority || "N/A"}</div></div>
              <div className="info-row"><div className="info-label">Track</div><div className="info-value"><span className="badge badge-navy">{TRACK_LABELS[p.service_track] || p.service_track}</span></div></div>
              <div className="info-row"><div className="info-label">Geo Tier</div><div className="info-value">{GEO_LABELS[p.geo_tier] || p.geo_tier}</div></div>
              {p.linkedin_url && <div className="info-row"><div className="info-label">LinkedIn</div><div className="info-value"><a href={p.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm" style={{ color: "var(--blue)" }}><ExternalLink size={12} /> Profile</a></div></div>}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3>LinkedIn Data</h3></div>
            <div className="card-body">
              {p.linkedin_headline && <div className="mb-4"><div className="form-label">Headline</div><div className="text-sm">{p.linkedin_headline}</div></div>}
              {p.linkedin_about && <div><div className="form-label">About</div><div className="text-sm text-secondary" style={{ maxHeight: 200, overflow: "auto", lineHeight: 1.6 }}>{p.linkedin_about}</div></div>}
              {!p.linkedin_headline && !p.linkedin_about && <div className="empty-state"><p>No LinkedIn data available</p></div>}
            </div>
          </div>
          <div className="card" style={{ gridColumn: "1 / -1" }}>
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>Company Assessment</h3>
              <button className="btn btn-secondary btn-sm" onClick={async () => {
                try {
                  showToast("Assessing company...", "info");
                  const { data: r } = await API.post(`/prospects/${p.id}/assess`);
                  showToast("Assessment complete", "success");
                  setData(prev => ({ ...prev, prospect: r.prospect }));
                } catch (err) { showToast(err.response?.data?.error || "Assessment failed", "error"); }
              }}>{p.company_assessment ? "Re-assess" : "Assess Company"}</button>
            </div>
            <div className="card-body">
              {p.company_assessment ? (<>
                <div className="mb-4">
                  <div className="form-label">Tracks</div>
                  <div className="flex gap-2 items-center" style={{ flexWrap: "wrap" }}>
                    <span className="badge badge-navy" style={{ background: TRACK_COLORS[p.service_track] }}>{TRACK_LABELS[p.service_track] || p.service_track} (Primary)</span>
                    {(() => { try { const st = JSON.parse(p.service_tracks || "[]"); return st.filter(t => t !== p.service_track).map(t => <span key={t} className="badge badge-gray" style={{ border: `1px solid ${TRACK_COLORS[t] || "#888"}`, color: TRACK_COLORS[t] || "#888" }}>{TRACK_LABELS[t] || t}</span>); } catch { return null; } })()}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="form-label">Assessment</div>
                  <div className="text-sm text-secondary" style={{ lineHeight: 1.6 }}>{p.company_assessment}</div>
                </div>
                {p.pain_points && (<div className="mb-4">
                  <div className="form-label">Pain Points</div>
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.8 }}>
                    {(() => { try { return JSON.parse(p.pain_points).map((pt, i) => <li key={i} style={{ color: "var(--text-secondary)" }}>{pt}</li>); } catch { return null; } })()}
                  </ul>
                </div>)}
                {p.ai_track_reasoning && p.ai_track_reasoning.includes("confidence") && (<div>
                  <div className="form-label">Confidence</div>
                  {(() => { const m = p.ai_track_reasoning.match(/confidence:\s*([\d.]+)/); const c = m ? Math.round(parseFloat(m[1]) * 100) : 0; return (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}><div style={{ width: `${c}%`, height: "100%", background: c >= 80 ? "#16a34a" : c >= 60 ? "#2563eb" : "#d97706", borderRadius: 4 }} /></div>
                      <span className="text-sm font-semibold">{c}%</span>
                    </div>
                  ); })()}
                </div>)}
              </>) : <div className="empty-state"><p>No assessment yet. Click "Assess Company" to analyze.</p></div>}
            </div>
          </div>
          {p.spam_score != null && p.spam_score > 0 && (
            <div className="card" style={{ gridColumn: "1 / -1" }}>
              <div className="card-header"><h3 style={{ color: p.spam_score >= 25 ? "#dc2626" : p.spam_score > 10 ? "#d97706" : "#16a34a" }}>Spam Check: Score {p.spam_score}</h3></div>
              <div className="card-body">
                {p.notes && p.notes.includes("Spam check") ? (
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.8 }}>
                    {p.notes.split("|").filter(s => s.includes("Spam check")).map(s => s.replace(/.*Spam check.*?:/, "").trim()).join("; ").split("; ").map((issue, i) => (
                      <li key={i} style={{ color: "var(--text-secondary)" }}>{issue}</li>
                    ))}
                  </ul>
                ) : <div className="text-sm text-secondary">No specific issues recorded</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "emails" && (
        <div className="flex-col gap-4">
          {[1,2,3,4].map(n => {
            const subjA = p[`email_${n}_subject`];
            const bodyA = p[`email_${n}_body`];
            const subjB = p[`email_${n}b_subject`];
            const bodyB = p[`email_${n}b_body`];
            if (!subjA && !bodyA) return null;
            const active = p.active_variant || "A";
            return (
              <div key={n} className="card">
                <div className="card-header"><h3>Email {n}</h3></div>
                <div className="card-body">
                  <div style={{ display: "grid", gridTemplateColumns: subjB ? "1fr 1fr" : "1fr", gap: 16 }}>
                    <div style={{ borderLeft: `3px solid ${active === "A" ? "#16a34a" : "#d1d5db"}`, paddingLeft: 12 }}>
                      <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                        <span className="text-xs font-semibold" style={{ color: active === "A" ? "#16a34a" : "#9ca3af" }}>Variant A{active === "A" ? " (Active)" : ""}</span>
                        <button className="btn btn-ghost btn-xs" onClick={() => copyToClipboard(bodyA, showToast)}><Copy size={12} /></button>
                      </div>
                      <div className="text-xs font-medium text-secondary" style={{ marginBottom: 4 }}>{subjA || "(no subject)"}</div>
                      <div className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{bodyA}</div>
                    </div>
                    {subjB && (
                      <div style={{ borderLeft: `3px solid ${active === "B" ? "#16a34a" : "#d1d5db"}`, paddingLeft: 12 }}>
                        <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                          <span className="text-xs font-semibold" style={{ color: active === "B" ? "#16a34a" : "#9ca3af" }}>Variant B{active === "B" ? " (Active)" : ""}</span>
                          <button className="btn btn-ghost btn-xs" onClick={() => copyToClipboard(bodyB, showToast)}><Copy size={12} /></button>
                        </div>
                        <div className="text-xs font-medium text-secondary" style={{ marginBottom: 4 }}>{subjB || "(no subject)"}</div>
                        <div className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>{bodyB}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {!p.email_1_body && <div className="card"><div className="card-body"><div className="empty-state"><Mail size={28} /><h3>No emails generated</h3><p>Use the AI Engine to generate personalized emails</p></div></div></div>}
        </div>
      )}

      {tab === "linkedin" && (
        <div className="flex-col gap-4">
          {p.linkedin_connection_note && (
            <div className="card">
              <div className="card-header"><h3>Connection Note</h3><button className="btn btn-ghost btn-xs" onClick={() => copyToClipboard(p.linkedin_connection_note, showToast)}><Copy size={12} /> Copy</button></div>
              <div className="card-body"><div className="text-sm" style={{ whiteSpace: "pre-wrap" }}>{p.linkedin_connection_note}</div></div>
            </div>
          )}
          {p.linkedin_message_1 && (
            <div className="card">
              <div className="card-header"><h3>LinkedIn Message 1</h3><button className="btn btn-ghost btn-xs" onClick={() => copyToClipboard(p.linkedin_message_1, showToast)}><Copy size={12} /> Copy</button></div>
              <div className="card-body"><div className="text-sm" style={{ whiteSpace: "pre-wrap" }}>{p.linkedin_message_1}</div></div>
            </div>
          )}
          {p.linkedin_message_2 && (
            <div className="card">
              <div className="card-header"><h3>LinkedIn Message 2</h3><button className="btn btn-ghost btn-xs" onClick={() => copyToClipboard(p.linkedin_message_2, showToast)}><Copy size={12} /> Copy</button></div>
              <div className="card-body"><div className="text-sm" style={{ whiteSpace: "pre-wrap" }}>{p.linkedin_message_2}</div></div>
            </div>
          )}
          {!p.linkedin_connection_note && !p.linkedin_message_1 && <div className="card"><div className="card-body"><div className="empty-state"><p>No LinkedIn messages generated</p></div></div></div>}
        </div>
      )}

      {tab === "activity" && (
        <div className="card">
          <div className="card-body" style={{ maxHeight: 500, overflowY: "auto" }}>
            {(activities || []).map(a => (
              <div key={a.id} className="timeline-item">
                <div className={`timeline-dot ${a.direction === 'inbound' ? 'inbound' : 'outbound'}`} />
                <div style={{ flex: 1 }}>
                  <div className="font-medium text-sm">{a.type?.replace(/_/g, " ")}</div>
                  {a.subject && <div className="text-sm text-secondary">{a.subject}</div>}
                  {a.body && <div className="text-xs text-tertiary mt-1" style={{ maxHeight: 60, overflow: "hidden" }}>{a.body.substring(0, 150)}...</div>}
                  <div className="timeline-meta">{a.channel} &middot; {new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
            {(!activities || activities.length === 0) && <div className="empty-state"><Clock size={28} /><p>No activity recorded</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CONTACTS DATABASE ============
function ContactsDB() {
  const [data, setData] = useState({ contacts: [], total: 0, pages: 0 });
  const [filters, setFilters] = useState({ page: 1, search: "", country: "", source_file: "", limit: 50 });
  const [stats, setStats] = useState(null);
  const [showToast, toastEl] = useToast();

  const load = useCallback(async () => {
    const params = { ...filters };
    if (params.search) { params.q = params.search; delete params.search; }
    Object.keys(params).forEach(k => { if (!params[k] && params[k] !== 0) delete params[k]; });
    try {
      const { data: d } = await API.get("/contacts-db", { params });
      // Handle both array response and paginated object response
      if (Array.isArray(d)) {
        setData({ contacts: d, total: d.length, pages: 1 });
      } else {
        const contacts = d.contacts || d.rows || d.data || [];
        const total = d.total || d.count || contacts.length;
        const pages = d.pages || Math.ceil(total / filters.limit) || 1;
        setData({ contacts, total, pages });
      }
    } catch {
      setData({ contacts: [], total: 0, pages: 0 });
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    API.get("/contacts-db/stats").then(r => setStats(r.data || {})).catch(() => {});
  }, []);

  const exportCSV = async () => {
    try {
      const all = await API.get("/contacts-db", { params: { limit: 10000 } });
      const list = Array.isArray(all.data) ? all.data : (all.data.contacts || all.data.rows || all.data.data || []);
      const rows = [["First Name","Last Name","Email","Company","Title","Country","Industry","Source","Email Status"]];
      list.forEach(c => rows.push([c.first_name,c.last_name,c.email,c.company_name,c.title,c.location_country,c.industry,c.source_file,c.email_status]));
      const csv = rows.map(r => r.map(v => `"${(v||'').toString().replace(/"/g,'""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "contacts_db_export.csv"; a.click();
      showToast(`Exported ${list.length} contacts`, "success");
    } catch { showToast("Export failed", "error"); }
  };

  const countries = stats?.by_country || [];
  const sources = stats?.by_source || [];
  const totalPages = data.pages || Math.ceil((data.total || 0) / filters.limit) || 1;

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div>
          <h2>Contacts Database</h2>
          <div className="page-subtitle">
            {stats ? `${stats.total?.toLocaleString()} total, ${stats.verified?.toLocaleString()} verified, ${stats.as_prospects?.toLocaleString()} as prospects` : `${data.total?.toLocaleString()} contacts`}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={14} /> Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="filter-bar">
        <input className="form-input" style={{ width: 260 }} placeholder="Search contacts..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))} />
        {countries.length > 0 && (
          <select className="form-select" style={{ width: 160 }} value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value, page: 1 }))}>
            <option value="">All Countries</option>
            {countries.map(c => <option key={c.country} value={c.country}>{c.country} ({c.count})</option>)}
          </select>
        )}
        {sources.length > 0 && (
          <select className="form-select" style={{ width: 220 }} value={filters.source_file} onChange={e => setFilters(f => ({ ...f, source_file: e.target.value, page: 1 }))}>
            <option value="">All Sources</option>
            {sources.map(s => <option key={s.source_file} value={s.source_file}>{s.source_file} ({s.count})</option>)}
          </select>
        )}
        <select className="form-select" style={{ width: 100 }} value={filters.limit} onChange={e => setFilters(f => ({ ...f, limit: parseInt(e.target.value), page: 1 }))}>
          <option value={50}>50 rows</option>
          <option value={100}>100 rows</option>
          <option value={250}>250 rows</option>
          <option value={500}>500 rows</option>
        </select>
      </div>

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Title</th><th>Country</th><th>Source</th><th>Status</th></tr></thead>
          <tbody>
            {(data.contacts || []).map((c, i) => (
              <tr key={c.id || i}>
                <td className="font-semibold text-primary">{c.first_name} {c.last_name}</td>
                <td className="text-sm">{c.email}</td>
                <td className="text-secondary">{c.company_name}</td>
                <td className="text-sm text-tertiary truncate" style={{ maxWidth: 160 }}>{c.title}</td>
                <td className="text-sm">{c.location_country}</td>
                <td><span className="badge badge-gray text-xs">{c.source_file}</span></td>
                <td>{c.email_status === "Verified" ? <span className="badge badge-green">verified</span> : <span className="badge badge-gray">{c.email_status || "unknown"}</span>}</td>
              </tr>
            ))}
            {(!data.contacts || data.contacts.length === 0) && <tr><td colSpan={7}><div className="empty-state"><Database size={28} /><h3>No contacts found</h3><p>Import data or sync from Apollo to populate</p></div></td></tr>}
          </tbody>
        </table>
        <div className="pagination">
          <button className="btn btn-secondary btn-sm" disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Previous</button>
          <span className="page-info">Page {filters.page} of {totalPages} ({data.total?.toLocaleString()} total)</span>
          <button className="btn btn-secondary btn-sm" disabled={filters.page >= totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next</button>
        </div>
      </div>
    </div>
  );
}

// ============ AI ENGINE ============
function AIEngine() {
  const [prospects, setProspects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]);
  const [showToast, toastEl] = useToast();

  const loadProspects = async () => {
    const { data } = await API.get("/prospects", { params: { status: "imported", limit: 100 } });
    let list = data.prospects || [];
    if (list.length === 0) {
      const { data: d2 } = await API.get("/prospects", { params: { status: "new", limit: 100 } });
      list = d2.prospects || [];
    }
    setProspects(list);
  };

  const loadJobs = async () => {
    try { const { data } = await API.get("/batch-jobs"); setBatchJobs(data || []); } catch {}
  };

  useEffect(() => { loadProspects(); loadJobs(); }, []);

  const generateEmails = async () => {
    if (selected.length === 0) return;
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await API.post("/ai/batch-generate", { prospect_ids: selected });
      setResult(data);
      showToast(`Generation started: ${data.total} prospects`, "success");
      // Poll for job status
      if (data.job_id) {
        const poll = setInterval(async () => {
          const { data: j } = await API.get(`/batch-jobs/${data.job_id}`);
          if (j.status === "completed" || j.status === "failed") {
            clearInterval(poll);
            loadProspects();
            loadJobs();
            showToast(j.status === "completed" ? "Generation complete" : "Generation failed", j.status === "completed" ? "success" : "error");
          }
        }, 5000);
      }
    } catch (e) { showToast("Generation failed", "error"); }
    setGenerating(false);
  };

  const generateSingle = async () => {
    if (selected.length === 0) return;
    setGenerating(true);
    try {
      const { data } = await API.post("/ai/generate-emails", { prospect_ids: selected });
      setResult(data);
      showToast(`Generated for ${data.results?.filter(r => r.success).length} prospects`, "success");
      loadProspects();
    } catch { showToast("Failed", "error"); }
    setGenerating(false);
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => selected.length === prospects.length ? setSelected([]) : setSelected(prospects.map(p => p.id));

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div>
          <h2>AI Engine</h2>
          <div className="page-subtitle">Generate personalized emails and LinkedIn messages</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={generateSingle} disabled={selected.length === 0 || generating}>
            <Brain size={14} /> Generate ({selected.length})
          </button>
          <button className="btn btn-primary" onClick={generateEmails} disabled={selected.length === 0 || generating}>
            <Zap size={14} /> {generating ? "Processing..." : `Batch Generate (${selected.length})`}
          </button>
        </div>
      </div>

      {result && (
        <div className="card mb-4">
          <div className="card-body flex items-center gap-3">
            <CheckCircle size={18} className="text-green" />
            <span className="font-medium text-sm">
              {result.message || `${result.results?.filter(r => r.success).length || 0} succeeded, ${result.results?.filter(r => r.error).length || 0} failed`}
            </span>
          </div>
        </div>
      )}

      {batchJobs.length > 0 && (
        <div className="card mb-4">
          <div className="card-header"><h3>Recent Jobs</h3></div>
          <div className="card-body">
            {batchJobs.slice(0, 5).map(j => (
              <div key={j.id} className="flex items-center gap-3 mb-3">
                <span className={`badge badge-${j.status === 'completed' ? 'green' : j.status === 'running' ? 'blue' : j.status === 'failed' ? 'red' : 'gray'}`}>{j.status}</span>
                <span className="text-sm">{j.type?.replace(/_/g, " ")}</span>
                <span className="text-xs text-tertiary">{j.processed_items || 0} / {j.total_items || 0}</span>
                {j.total_items > 0 && <div style={{ flex: 1, maxWidth: 150 }}><div className="progress-bar"><div className={`progress-bar-fill ${j.status === 'completed' ? 'green' : ''}`} style={{ width: `${Math.min(100, ((j.processed_items || 0) / j.total_items) * 100)}%` }} /></div></div>}
                <span className="text-xs text-tertiary">{j.created_at ? new Date(j.created_at).toLocaleString() : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Prospects Ready for Processing ({prospects.length})</h3>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" onClick={loadProspects}><RefreshCw size={14} /></button>
            <button className="btn btn-secondary btn-sm" onClick={selectAll}>{selected.length === prospects.length && prospects.length > 0 ? "Deselect All" : "Select All"}</button>
          </div>
        </div>
        <table className="data-table">
          <thead><tr><th style={{ width: 40 }}></th><th>Name</th><th>Company</th><th>Tracks</th><th>Email</th><th>Emails</th></tr></thead>
          <tbody>
            {prospects.map(p => {
              let secondaryTracks = [];
              try { const st = JSON.parse(p.service_tracks || "[]"); secondaryTracks = st.filter(t => t !== p.service_track); } catch {}
              return (
                <tr key={p.id}>
                  <td><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} /></td>
                  <td className="font-semibold text-primary">{p.first_name} {p.last_name}</td>
                  <td className="text-secondary">{p.company}</td>
                  <td>
                    <span className="badge badge-navy">{p.service_track}</span>
                    {secondaryTracks.map(t => <span key={t} className="badge" style={{ fontSize: 9, marginLeft: 3, background: "transparent", border: `1px solid ${TRACK_COLORS[t] || "#888"}`, color: TRACK_COLORS[t] || "#888" }}>{t}</span>)}
                  </td>
                  <td className="text-sm">{p.email || <span className="text-tertiary">no email</span>}</td>
                  <td>{p.email_1_body ? <span className="badge badge-green">generated</span> : <span className="badge badge-gray">pending</span>}</td>
                </tr>
              );
            })}
            {prospects.length === 0 && <tr><td colSpan={6}><div className="empty-state"><Brain size={28} /><h3>No prospects to process</h3><p>Import data first, then come here to generate</p></div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ SMART IMPORT ============
function SmartImport() {
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [countries, setCountries] = useState(["United Arab Emirates"]);
  const [seniorities, setSeniorities] = useState(["owner", "founder", "c_suite", "director", "vp", "head"]);
  const [employeeRanges, setEmployeeRanges] = useState(["1,10", "11,20", "21,50", "51,200", "201,500", "501,1000", "1001,5000"]);
  const [maxResults, setMaxResults] = useState(100);
  const [campaignTag, setCampaignTag] = useState("UAE C-Suite");
  const [customTag, setCustomTag] = useState("");
  const [campaignReady, setCampaignReady] = useState("ready");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [showToast, toastEl] = useToast();

  const COUNTRY_OPTIONS = [
    { value: "United Arab Emirates", label: "UAE" },
    { value: "Saudi Arabia", label: "Saudi Arabia" },
    { value: "Qatar", label: "Qatar" },
    { value: "Kuwait", label: "Kuwait" },
    { value: "Bahrain", label: "Bahrain" },
    { value: "Oman", label: "Oman" },
    { value: "Lebanon", label: "Lebanon" }
  ];
  const SENIORITY_OPTIONS = [
    { value: "owner", label: "Owner" },
    { value: "founder", label: "Founder" },
    { value: "c_suite", label: "C-Suite" },
    { value: "director", label: "Director" },
    { value: "vp", label: "VP" },
    { value: "head", label: "Head" }
  ];
  const EMPLOYEE_OPTIONS = [
    { value: "1,10", label: "1-10" },
    { value: "11,20", label: "11-20" },
    { value: "21,50", label: "21-50" },
    { value: "51,200", label: "51-200" },
    { value: "201,500", label: "201-500" },
    { value: "501,1000", label: "501-1000" },
    { value: "1001,5000", label: "1001-5000" }
  ];
  const TAG_OPTIONS = ["UAE C-Suite", "GCC Investors", "Lebanon CEOs", "custom"];

  const toggleMulti = (arr, setArr, val) => setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  const loadCredits = () => {
    setCreditsLoading(true);
    API.get("/apollo/credits").then(r => setCredits(r.data)).catch(() => setCredits(null)).finally(() => setCreditsLoading(false));
  };

  const loadHistory = () => {
    API.get("/apollo/import-history").then(r => setHistory(r.data || [])).catch(() => {});
  };

  useEffect(() => { loadCredits(); loadHistory(); }, []);

  useEffect(() => {
    const hasRunning = history.some(j => j.status === "running");
    if (!hasRunning) return;
    const interval = setInterval(loadHistory, 10000);
    return () => clearInterval(interval);
  }, [history]);

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const params = new URLSearchParams();
      if (countries.length) params.set("countries", countries.join(","));
      if (seniorities.length) params.set("seniorities", seniorities.join(","));
      if (employeeRanges.length) params.set("employeeRanges", employeeRanges.join(","));
      const { data } = await API.get("/apollo/smart-import/preview?" + params.toString());
      setPreview(data);
    } catch (e) { showToast("Preview failed: " + (e.response?.data?.error || e.message), "error"); }
    setPreviewing(false);
  };

  const doImport = async () => {
    setImporting(true);
    try {
      const tag = campaignTag === "custom" ? customTag : campaignTag;
      const { data } = await API.post("/apollo/smart-import", {
        countries, seniorities, employeeRanges, maxResults,
        campaign_tag: tag, campaign_ready: campaignReady
      });
      showToast(`Import started! Job ID: ${data.job_id}`, "success");
      setPreview(null);
      setTimeout(loadHistory, 2000);
    } catch (e) { showToast("Import failed: " + (e.response?.data?.error || e.message), "error"); }
    setImporting(false);
  };

  const credColor = credits?.credits_remaining > 1000 ? "#16a34a" : credits?.credits_remaining >= 500 ? "#d97706" : "#dc2626";
  const credBadge = credits?.credits_remaining > 1000 ? "badge-green" : credits?.credits_remaining >= 500 ? "badge-amber" : "badge-red";

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div>
          <h2>Smart Import</h2>
          <div className="page-subtitle">Import prospects from Apollo with intelligent filtering</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => { loadCredits(); loadHistory(); }}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Apollo Credits */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Apollo Credits</h3></div>
        <div className="card-body">
          {creditsLoading ? (
            <span className="text-sm text-tertiary">Loading credits...</span>
          ) : credits ? (
            <div className="flex items-center gap-4">
              <div style={{ fontSize: 36, fontWeight: 700, color: credColor }}>{credits.credits_remaining?.toLocaleString()}</div>
              <div>
                <div className="text-sm text-secondary">credits remaining</div>
                <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                  <span className={`badge ${credBadge}`}>{credits.credits_remaining > 1000 ? "Healthy" : credits.credits_remaining >= 500 ? "Low" : "Critical"}</span>
                  {credits.plan_name && <span className="text-xs text-tertiary">{credits.plan_name}</span>}
                  {credits.credits_used != null && <span className="text-xs text-tertiary">({credits.credits_used?.toLocaleString()} used)</span>}
                </div>
              </div>
            </div>
          ) : (
            <span className="text-sm" style={{ color: "#dc2626" }}>Could not load Apollo credits</span>
          )}
        </div>
      </div>

      {/* Preview Search */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Search & Import</h3></div>
        <div className="card-body">
          <div className="form-group">
            <label className="form-label">Countries</label>
            <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
              {COUNTRY_OPTIONS.map(c => (
                <label key={c.value} className="flex items-center gap-1 text-sm" style={{ cursor: "pointer" }}>
                  <input type="checkbox" checked={countries.includes(c.value)} onChange={() => toggleMulti(countries, setCountries, c.value)} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Seniorities</label>
            <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
              {SENIORITY_OPTIONS.map(s => (
                <label key={s.value} className="flex items-center gap-1 text-sm" style={{ cursor: "pointer" }}>
                  <input type="checkbox" checked={seniorities.includes(s.value)} onChange={() => toggleMulti(seniorities, setSeniorities, s.value)} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Employee Ranges</label>
            <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
              {EMPLOYEE_OPTIONS.map(e => (
                <label key={e.value} className="flex items-center gap-1 text-sm" style={{ cursor: "pointer" }}>
                  <input type="checkbox" checked={employeeRanges.includes(e.value)} onChange={() => toggleMulti(employeeRanges, setEmployeeRanges, e.value)} />
                  {e.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="form-group">
              <label className="form-label">Max Results</label>
              <input className="form-input" type="number" value={maxResults} onChange={e => setMaxResults(parseInt(e.target.value) || 100)} min={1} max={10000} />
            </div>
            <div className="form-group">
              <label className="form-label">Campaign Tag</label>
              <select className="form-select" value={campaignTag} onChange={e => setCampaignTag(e.target.value)}>
                {TAG_OPTIONS.map(t => <option key={t} value={t}>{t === "custom" ? "Custom..." : t}</option>)}
              </select>
              {campaignTag === "custom" && (
                <input className="form-input" style={{ marginTop: 6 }} placeholder="Enter custom tag" value={customTag} onChange={e => setCustomTag(e.target.value)} />
              )}
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Campaign Ready</label>
            <select className="form-select" value={campaignReady} onChange={e => setCampaignReady(e.target.value)} style={{ maxWidth: 200 }}>
              <option value="ready">Ready</option>
              <option value="needs_review">Needs Review</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="flex gap-2" style={{ marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={doPreview} disabled={previewing || countries.length === 0}>
              <Search size={14} /> {previewing ? "Searching..." : "Preview"}
            </button>
            <button className="btn btn-primary" onClick={doImport} disabled={!preview || importing}>
              <Download size={14} /> {importing ? "Importing..." : "Import"}
            </button>
          </div>

          {preview && (
            <div style={{ marginTop: 16, padding: 16, background: "var(--bg-secondary, #f9fafb)", borderRadius: "var(--radius)" }}>
              <div className="flex items-center gap-2 mb-3">
                <Eye size={16} className="text-secondary" />
                <span className="font-semibold text-sm">Preview: {preview.total_available?.toLocaleString()} contacts available</span>
              </div>
              {(preview.sample || []).length > 0 && (
                <table className="data-table">
                  <thead><tr><th>Name</th><th>Title</th><th>Company</th><th>Country</th></tr></thead>
                  <tbody>
                    {preview.sample.map((s, i) => (
                      <tr key={i}>
                        <td className="font-semibold text-primary">{s.name}</td>
                        <td className="text-secondary text-sm">{s.title}</td>
                        <td className="text-sm">{s.company}</td>
                        <td className="text-sm">{s.country}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Import History */}
      <div className="card">
        <div className="card-header">
          <h3>Import History</h3>
          <button className="btn btn-ghost btn-sm" onClick={loadHistory}><RefreshCw size={14} /></button>
        </div>
        {history.length > 0 ? (
          <div className="table-scroll-wrapper">
            <table className="data-table">
              <thead><tr><th>Job ID</th><th>Status</th><th>Total</th><th>Imported</th><th>Started</th><th>Completed</th></tr></thead>
              <tbody>
                {history.map(j => (
                  <tr key={j.id}>
                    <td className="font-medium">#{j.id}</td>
                    <td>
                      <span className={`badge badge-${j.status === "completed" ? "green" : j.status === "running" ? "blue" : j.status === "failed" ? "red" : "gray"}`}>
                        {j.status}
                      </span>
                    </td>
                    <td>{j.total_items || 0}</td>
                    <td>{j.processed_items || 0}</td>
                    <td className="text-sm text-secondary">{j.started_at ? new Date(j.started_at).toLocaleString() : "-"}</td>
                    <td className="text-sm text-secondary">{j.completed_at ? new Date(j.completed_at).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card-body"><div className="empty-state"><Database size={28} /><h3>No imports yet</h3><p>Run your first smart import above</p></div></div>
        )}
      </div>
    </div>
  );
}

// ============ IMPORT ============
function Import() {
  const [file, setFile] = useState(null);
  const [track, setTrack] = useState("A");
  const [geo, setGeo] = useState("1");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showToast, toastEl] = useToast();

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    const text = await file.text();
    let prospects;
    if (file.name.endsWith(".json")) {
      prospects = JSON.parse(text);
    } else {
      const lines = text.split("\n").filter(l => l.trim());
      const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      prospects = lines.slice(1).map(line => {
        const vals = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || "").replace(/^"|"$/g, "").trim(); });
        return obj;
      });
    }
    try {
      const { data } = await API.post("/prospects/import", { prospects, service_track: track, geo_tier: geo });
      setResult(data);
      showToast(`Imported ${data.imported} prospects`, "success");
    } catch (e) { showToast("Import failed", "error"); }
    setLoading(false);
  };

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header"><h2>Import Prospects</h2></div>
      <div className="card">
        <div className="card-body">
          <p className="text-sm text-secondary mb-4">Upload a JSON or CSV file with prospect data. Required: first_name, last_name, email. The system auto-detects track and geo from title, industry, and country if not specified.</p>
          <div className="grid-2 mb-4">
            <div className="form-group">
              <label className="form-label">Default Service Track</label>
              <select className="form-select" value={track} onChange={e => setTrack(e.target.value)}>
                {Object.entries(TRACK_LABELS).map(([k, v]) => <option key={k} value={k}>{k}: {v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Default Geographic Tier</label>
              <select className="form-select" value={geo} onChange={e => setGeo(e.target.value)}>
                {Object.entries(GEO_LABELS).map(([k, v]) => <option key={k} value={k}>Tier {k}: {v}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">File (JSON or CSV)</label>
            <input type="file" accept=".json,.csv,.txt" onChange={e => setFile(e.target.files[0])} style={{ fontSize: 13 }} />
          </div>
          <button className="btn btn-primary" onClick={handleImport} disabled={!file || loading}>
            <Upload size={14} /> {loading ? "Importing..." : "Import Prospects"}
          </button>
          {result && (
            <div className="mt-4" style={{ padding: 16, background: "var(--green-bg)", borderRadius: "var(--radius)" }}>
              <div className="flex items-center gap-2"><CheckCircle size={16} className="text-green" /><span className="font-semibold text-sm text-green">Import Complete</span></div>
              <div className="text-sm mt-2 text-secondary">Imported: {result.imported} | Duplicates: {result.dupes || 0} | Skipped: {result.skipped || 0} | Total in file: {result.total}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ MEETINGS ============
function Meetings() {
  const [meetings, setMeetings] = useState([]);
  useEffect(() => { API.get("/meetings").then(r => setMeetings(r.data)).catch(() => {}); }, []);

  return (
    <div className="main-content">
      <div className="page-header"><h2>Meetings</h2><div className="page-subtitle">{meetings.length} total</div></div>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>Prospect</th><th>Company</th><th>Track</th><th>Assigned To</th><th>Date</th><th>Status</th><th>Topic</th></tr></thead>
          <tbody>
            {meetings.map(m => (
              <tr key={m.id}>
                <td className="font-semibold text-primary">{m.first_name} {m.last_name}</td>
                <td className="text-secondary">{m.company}</td>
                <td><span className="badge badge-navy">{TRACK_LABELS[m.service_track] || m.service_track}</span></td>
                <td className="text-sm">{m.assigned_to || m.partner}</td>
                <td className="text-sm">{m.scheduled_at ? new Date(m.scheduled_at).toLocaleDateString() : "TBD"}</td>
                <td><span className={`badge badge-${m.status === "completed" ? "green" : m.status === "scheduled" ? "blue" : "gray"}`}>{m.status}</span></td>
                <td className="text-sm text-tertiary truncate" style={{ maxWidth: 200 }}>{m.discussion_topic || ""}</td>
              </tr>
            ))}
            {meetings.length === 0 && <tr><td colSpan={7}><div className="empty-state"><Calendar size={28} /><h3>No meetings yet</h3><p>Meetings appear here when prospects book via Calendly</p></div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ DEALS ============
function Deals() {
  const [deals, setDeals] = useState([]);
  useEffect(() => { API.get("/deals").then(r => setDeals(r.data)).catch(() => {}); }, []);
  const stages = ["discovery", "proposal", "negotiation", "won"];

  return (
    <div className="main-content">
      <div className="page-header"><h2>Deal Pipeline</h2></div>
      <div className="kanban-container">
        {stages.map(stage => (
          <div key={stage} className="kanban-column">
            <div className="kanban-column-header">
              <span className={`badge badge-${stage === "won" ? "green" : "navy"}`}>{stage}</span>
              <span className="kanban-column-count">{deals.filter(d => d.stage === stage).length}</span>
            </div>
            {deals.filter(d => d.stage === stage).map(d => (
              <div key={d.id} className="kanban-card">
                <div className="font-semibold text-sm">{d.title || `${d.first_name} ${d.last_name}`}</div>
                <div className="text-xs text-tertiary mt-1">{d.company}</div>
                {d.value_aed > 0 && <div className="font-bold mt-2 text-green">AED {d.value_aed?.toLocaleString()}</div>}
                <div className="text-xs text-tertiary mt-2">{d.assigned_partner}</div>
              </div>
            ))}
            {deals.filter(d => d.stage === stage).length === 0 && <div className="empty-state" style={{ padding: 24 }}><p className="text-xs">Empty</p></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ CAMPAIGNS ============
const JOURNEY_STAGES = [
  { key: "queued", label: "Queued" },
  { key: "email_1_sent", label: "Email 1" },
  { key: "email_2_sent", label: "Email 2" },
  { key: "email_3_sent", label: "Email 3" },
  { key: "email_4_sent", label: "Email 4" },
  { key: "replied", label: "Replied" },
  { key: "meeting_scheduled", label: "Meeting" },
  { key: "completed", label: "Done" }
];
const FAILED_STAGES = ["blocked", "bounced", "unsubscribed"];

function stageIndex(stage) {
  const openedMap = { email_1_opened: "email_1_sent", email_2_opened: "email_2_sent", email_3_opened: "email_3_sent", email_4_opened: "email_4_sent" };
  const mapped = openedMap[stage] || stage;
  const idx = JOURNEY_STAGES.findIndex(s => s.key === mapped);
  return idx >= 0 ? idx : 0;
}

function JourneyBar({ stage }) {
  const isFailed = FAILED_STAGES.includes(stage);
  const isOpened = stage.includes("_opened");
  const currentIdx = stageIndex(stage);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, width: "100%" }}>
      {JOURNEY_STAGES.map((s, i) => {
        const isComplete = i < currentIdx;
        const isCurrent = i === currentIdx;
        let bg = "#e5e7eb";
        if (isFailed && isCurrent) bg = "#dc2626";
        else if (isComplete) bg = "#16a34a";
        else if (isCurrent && isOpened) bg = "#0d9488";
        else if (isCurrent) bg = "#2563eb";
        const lineBg = i < currentIdx ? "#16a34a" : "#e5e7eb";
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <div style={{ flex: 1, height: 2, background: lineBg, minWidth: 8 }} />}
            <div title={s.label + (isFailed && isCurrent ? " (" + stage + ")" : "")} style={{ width: 14, height: 14, borderRadius: "50%", background: bg, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#fff", fontWeight: 700 }}>
              {isFailed && isCurrent ? "X" : isComplete ? "\u2713" : ""}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function Campaigns() {
  const [view, setView] = useState("list");
  const [detailId, setDetailId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total_generated: 0, by_track: [] });
  const [form, setForm] = useState({ name: "Campaign " + new Date().toISOString().slice(0, 10), service_track: "", geo_tier: "", country: "", company: "", title: "", emails: "", limit: 50 });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showToast, toastEl] = useToast();
  const [showAddProspects, setShowAddProspects] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addSelected, setAddSelected] = useState(new Set());
  const [addLoading, setAddLoading] = useState(false);
  const [abTest, setAbTest] = useState(null);

  const TRACK_OPTS = [["", "All Tracks"], ["C", "C - Consultancy"], ["I", "I - Investments"], ["A", "A - Accounting"], ["T", "T - Transaction Advisory"], ["F", "F - Family Office"], ["E", "E - Execution"]];
  const GEO_OPTS = [["", "All Geos"], ["1", "Tier 1 - UAE"], ["2", "Tier 2 - GCC"], ["3", "Tier 3 - Lebanon"], ["4", "Tier 4 - Africa"]];

  const fetchCampaigns = useCallback(() => {
    setLoading(true);
    API.get("/smartlead/campaigns").then(r => { setCampaigns(Array.isArray(r.data) ? r.data : []); setError(null); })
      .catch(err => { setError(err.response?.data?.error || "Failed to load"); setCampaigns([]); })
      .finally(() => setLoading(false));
  }, []);

  const fetchStats = useCallback(() => {
    API.post("/campaigns/preview", {}).then(r => setStats({ total_generated: r.data.total_generated, by_track: r.data.by_track })).catch(() => {});
    API.get("/analytics/ab-test").then(r => setAbTest(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchCampaigns(); fetchStats(); const iv = setInterval(fetchCampaigns, 30000); return () => clearInterval(iv); }, [fetchCampaigns, fetchStats]);

  const doPreview = async () => {
    setPreviewLoading(true); setLaunchResult(null);
    const body = { ...form, limit: Number(form.limit) || 50 };
    if (form.emails.trim()) body.emails = form.emails.split(",").map(e => e.trim()).filter(Boolean);
    else delete body.emails;
    if (!body.service_track) delete body.service_track;
    if (!body.geo_tier) delete body.geo_tier;
    if (!body.country) delete body.country;
    if (!body.company) delete body.company;
    if (!body.title) delete body.title;
    try { const { data } = await API.post("/campaigns/preview", body); setPreview(data); } catch { showToast("Preview failed", "error"); }
    finally { setPreviewLoading(false); }
  };

  const doLaunch = async () => {
    if (!form.name.trim()) return showToast("Campaign name required", "error");
    if (!window.confirm(`Launch campaign "${form.name}" with up to ${form.limit} prospects?`)) return;
    setLaunching(true); setLaunchResult(null);
    const body = { ...form, limit: Number(form.limit) || 50 };
    if (form.emails.trim()) body.emails = form.emails.split(",").map(e => e.trim()).filter(Boolean);
    else delete body.emails;
    if (!body.service_track) delete body.service_track;
    if (!body.geo_tier) delete body.geo_tier;
    if (!body.country) delete body.country;
    if (!body.company) delete body.company;
    if (!body.title) delete body.title;
    try {
      const { data } = await API.post("/campaigns/launch", body);
      setLaunchResult(data);
      if (data.launched > 0) showToast(`Launched ${data.launched} campaigns`, "success");
      else showToast(data.message || "No prospects matched", "error");
      fetchCampaigns(); fetchStats();
    } catch (err) { showToast(err.response?.data?.error || "Launch failed", "error"); }
    finally { setLaunching(false); }
  };

  const campaignAction = async (id, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm("Delete this campaign permanently?")) return;
        await API.delete(`/smartlead/campaigns/${id}`);
        showToast("Campaign deleted", "success");
      } else {
        await API.post(`/smartlead/campaigns/${id}/status`, { status: action });
        showToast(`Campaign ${action === "START" ? "started" : "paused"}`, "success");
      }
      fetchCampaigns();
    } catch (err) { showToast(err.response?.data?.error || "Action failed", "error"); }
  };

  const openDetail = async (id) => {
    setView("detail"); setDetailId(id); setDetailLoading(true); setDetail(null);
    try { const { data } = await API.get(`/smartlead/campaigns/${id}/detail`); setDetail(data); }
    catch (err) { showToast("Failed to load detail", "error"); }
    finally { setDetailLoading(false); }
  };

  const refreshDetail = useCallback(async () => {
    if (!detailId) return;
    try { const { data } = await API.get(`/smartlead/campaigns/${detailId}/detail`); setDetail(data); } catch {}
  }, [detailId]);

  useEffect(() => { if (view === "detail" && detailId) { const iv = setInterval(refreshDetail, 30000); return () => clearInterval(iv); } }, [view, detailId, refreshDetail]);

  const searchProspects = async (q) => {
    setAddSearch(q);
    if (q.length < 2) { setAddResults([]); return; }
    try { const { data } = await API.get("/prospects", { params: { search: q, status: "generated", limit: 20 } }); setAddResults(data.prospects || []); } catch { setAddResults([]); }
  };
  const addProspectsToCampaign = async () => {
    if (addSelected.size === 0 || !detailId) return;
    setAddLoading(true);
    try {
      const { data: r } = await API.post(`/campaigns/${detailId}/add-prospects`, { prospect_ids: [...addSelected] });
      if (r.added > 0) showToast(`Added ${r.added} prospects`, "success");
      else showToast(r.message || "No prospects added", "error");
      setShowAddProspects(false); setAddSelected(new Set()); setAddResults([]); setAddSearch(""); refreshDetail();
    } catch (err) { showToast(err.response?.data?.error || "Failed", "error"); }
    finally { setAddLoading(false); }
  };

  const statusBadge = (s) => { const str = String(s || "").toLowerCase(); if (str === "completed" || str === "started" || str === "active") return "green"; if (str === "paused" || str === "drafted") return "amber"; return "gray"; };

  if (view === "detail") {
    const c = detail?.campaign; const ld = detail?.leads; const st = detail?.stats;
    const _sentCount = Number(st?.sent_count || c?.sent_count || 0);
    const _bounceCount = Number(st?.bounce_count || 0);
    const _bounceRate = _sentCount > 0 ? (_bounceCount / _sentCount) * 100 : 0;
    const _bounceColor = _bounceRate < 1 ? "#16a34a" : _bounceRate <= 3 ? "#d97706" : "#dc2626";
    const _isAutoPaused = c?.status === "PAUSED" && _bounceRate > 3;
    return (
      <div className="main-content">
        {toastEl}
        <div className="page-header">
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => setView("list")} style={{ marginBottom: 4 }}>Back to Campaigns</button>
            <h2>{c?.name || "Campaign"} <span className={`badge badge-${statusBadge(c?.status)}`}>{c?.status || ""}</span>{_isAutoPaused && <span className="badge badge-red" style={{ marginLeft: 6, fontSize: 11 }}>Auto-paused</span>}</h2>
          </div>
          <div className="flex gap-2">
            {c?.status !== "ACTIVE" && <button className="btn btn-sm" style={{ background: "#16a34a", color: "#fff" }} onClick={() => { campaignAction(detailId, "START"); setTimeout(refreshDetail, 1000); }}>Start</button>}
            {c?.status === "ACTIVE" && <button className="btn btn-sm" style={{ background: "#d97706", color: "#fff" }} onClick={() => { campaignAction(detailId, "PAUSED"); setTimeout(refreshDetail, 1000); }}>Pause</button>}
            <button className="btn btn-sm" style={{ background: "#dc2626", color: "#fff" }} onClick={() => { campaignAction(detailId, "delete"); setView("list"); }}>Delete</button>
            <div style={{ position: "relative" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddProspects(v => !v)}><Plus size={14} /> Add Prospects</button>
              {showAddProspects && (
                <div style={{ position: "absolute", right: 0, top: "100%", marginTop: 4, background: "var(--bg-card, #fff)", border: "1px solid var(--border)", borderRadius: "var(--radius, 6px)", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", width: 360, zIndex: 100, padding: 12 }}>
                  <input className="form-input" placeholder="Search generated prospects..." value={addSearch} onChange={e => searchProspects(e.target.value)} style={{ marginBottom: 8 }} />
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {addResults.length === 0 ? <div className="text-tertiary text-sm">{addSearch.length < 2 ? "Type to search..." : "No results"}</div> : addResults.map(p => (
                      <div key={p.id} className="clickable" style={{ padding: "6px 0", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }} onClick={() => setAddSelected(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })}>
                        <input type="checkbox" checked={addSelected.has(p.id)} readOnly />
                        <div><span className="font-semibold">{p.first_name} {p.last_name}</span> <span className="text-secondary">- {p.company}</span></div>
                      </div>
                    ))}
                  </div>
                  {addSelected.size > 0 && <button className="btn btn-primary btn-sm" style={{ marginTop: 8, width: "100%" }} onClick={addProspectsToCampaign} disabled={addLoading}>{addLoading ? "Adding..." : `Add ${addSelected.size} Selected`}</button>}
                </div>
              )}
            </div>
          </div>
        </div>
        {detailLoading ? <div className="card"><div className="card-body text-tertiary">Loading...</div></div> : detail && <>
          <div className="stats-grid" style={{ gridTemplateColumns: "repeat(5, 1fr)", marginBottom: 16 }}>
            {[["Sent", st?.sent_count ?? c?.sent_count ?? 0], ["Opened", st?.open_count ?? 0, (Number(st?.sent_count) > 0) ? Math.round(((st?.open_count || 0) / st.sent_count) * 100) + "%" : "-"], ["Clicked", st?.click_count ?? 0, (Number(st?.sent_count) > 0) ? Math.round(((st?.click_count || 0) / st.sent_count) * 100) + "%" : "-"], ["Replied", st?.reply_count ?? 0, (Number(st?.sent_count) > 0) ? Math.round(((st?.reply_count || 0) / st.sent_count) * 100) + "%" : "-"], ["Bounced", st?.bounce_count ?? 0, _sentCount > 0 ? _bounceRate.toFixed(1) + "%" : "-", _bounceColor]].map(([label, val, pct, color]) => (
              <div key={label} className="card"><div className="card-body" style={{ textAlign: "center" }}><div className="text-sm text-secondary">{label}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{val}</div>{pct && <div className="text-sm" style={{ color: color || "var(--text-secondary)", fontWeight: color ? 600 : 400 }}>{pct}</div>}</div></div>
            ))}
          </div>
          <div className="card">
            <div className="card-header"><h3>Leads ({ld?.total_leads ?? 0})</h3></div>
            <div className="table-scroll-wrapper">
              {(!ld?.data || ld.data.length === 0) ? <div className="empty-state"><Users size={24} /><p>No leads in this campaign</p></div> : (
                <table className="data-table">
                  <thead><tr><th>Name</th><th>Email</th><th>Status</th><th style={{textAlign:"center"}}>Email 1</th><th style={{textAlign:"center"}}>Email 2</th><th style={{textAlign:"center"}}>Email 3</th><th style={{textAlign:"center"}}>Email 4</th><th>Reply</th><th>Meeting</th></tr></thead>
                  <tbody>{ld.data.map((l, i) => {
                    const ld2 = l.lead || {};
                    const ls = (l.status || l.lead_status || "").toUpperCase();
                    const sent = l.sent_count || l.email_sent || 0;
                    const opened = l.open_count || l.email_open || 0;
                    const clicked = l.click_count || l.email_click || 0;
                    const replied = l.reply_count || l.email_reply || 0;
                    const isBounced = ls === "BOUNCED";
                    const name = ((ld2.first_name || l.first_name || "") + " " + (ld2.last_name || l.last_name || "")).trim();
                    const email = ld2.email || l.email || l.lead_email || "";
                    const emailDot = (n) => {
                      if (isBounced && n <= sent) return <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"#dc2626"}} title="Bounced" />;
                      if (n > sent) return <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"#e5e7eb"}} title="Not sent" />;
                      if (clicked > 0 && n <= sent) return <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"#16a34a"}} title="Clicked" />;
                      if (opened > 0 && n <= sent) return <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"#0d9488"}} title="Opened" />;
                      return <span style={{display:"inline-block",width:10,height:10,borderRadius:"50%",background:"#2563eb"}} title="Sent" />;
                    };
                    return <tr key={i}>
                      <td className="font-semibold">{name || "Unknown"}</td>
                      <td className="text-sm text-secondary">{email}</td>
                      <td><span className={`badge badge-${ls === "COMPLETED" ? "green" : ls === "BLOCKED" || ls === "BOUNCED" ? "red" : ls === "ACTIVE" || ls === "STARTED" ? "blue" : "gray"}`}>{ls || "pending"}</span></td>
                      <td style={{textAlign:"center"}}>{emailDot(1)}</td>
                      <td style={{textAlign:"center"}}>{emailDot(2)}</td>
                      <td style={{textAlign:"center"}}>{emailDot(3)}</td>
                      <td style={{textAlign:"center"}}>{emailDot(4)}</td>
                      <td>{replied > 0 ? <span className="badge badge-green">Replied</span> : ""}</td>
                      <td></td>
                    </tr>;
                  })}</tbody>
                </table>
              )}
            </div>
          </div>
        </>}
      </div>
    );
  }

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div><h2>Campaigns</h2><div className="page-subtitle">Create and manage Smartlead email campaigns</div></div>
        <button className="btn btn-secondary btn-sm" onClick={fetchCampaigns}><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div><span style={{ fontSize: 24, fontWeight: 700 }}>{stats.total_generated}</span> <span className="text-secondary">prospects ready to launch</span></div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {stats.by_track.map(t => <span key={t.service_track} className="badge badge-blue">{t.service_track}: {t.c}</span>)}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Create Campaign</h3></div>
        <div className="card-body">
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="form-group"><label className="form-label">Campaign Name *</label><input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Max Prospects</label><input className="form-input" type="number" value={form.limit} onChange={e => setForm({ ...form, limit: e.target.value })} /></div>
          </div>
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="form-group"><label className="form-label">Service Track</label><select className="form-input" value={form.service_track} onChange={e => setForm({ ...form, service_track: e.target.value })}>{TRACK_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Geography</label><select className="form-input" value={form.geo_tier} onChange={e => setForm({ ...form, geo_tier: e.target.value })}>{GEO_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="form-group"><label className="form-label">Country</label><input className="form-input" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="e.g. United Arab Emirates" /></div>
            <div className="form-group"><label className="form-label">Company</label><input className="form-input" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Filter by company name" /></div>
          </div>
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="form-group"><label className="form-label">Title Filter</label><input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. CEO, Director" /></div>
            <div className="form-group"><label className="form-label">Specific Emails (comma-separated)</label><textarea className="form-input" rows={2} value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} placeholder="email1@co.com, email2@co.com" /></div>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-sm" onClick={doPreview} disabled={previewLoading}>{previewLoading ? "Loading..." : <><Eye size={14} /> Preview</>}</button>
            <button className="btn btn-primary btn-sm" onClick={doLaunch} disabled={launching}>{launching ? "Launching..." : <><Zap size={14} /> Launch Campaign</>}</button>
          </div>

          {launchResult && (
            <div style={{ marginTop: 12, padding: 12, background: launchResult.launched > 0 ? "var(--green-bg, #f0fdf4)" : "var(--red-bg, #fef2f2)", borderRadius: "var(--radius)", fontSize: 14 }}>
              <strong>{launchResult.launched > 0 ? `Launched ${launchResult.launched}/${launchResult.total} campaigns` : launchResult.message || "No prospects matched"}</strong>
              {launchResult.results && <div style={{ marginTop: 8 }}>{launchResult.results.map((r, i) => <div key={i} className="text-sm">{r.name || r.email}: {r.status || r.error}</div>)}</div>}
            </div>
          )}

          {preview && (
            <div style={{ marginTop: 12 }}>
              <div className="text-sm text-secondary" style={{ marginBottom: 8 }}>{preview.total_matching} prospects match filters (of {preview.total_generated} total generated)</div>
              {preview.prospects.length > 0 && (
                <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
                  <table className="data-table"><thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Title</th><th>Track</th></tr></thead>
                    <tbody>{preview.prospects.map(p => <tr key={p.id}><td className="font-semibold">{p.first_name} {p.last_name}</td><td className="text-sm text-secondary">{p.email}</td><td>{p.company}</td><td className="text-sm">{p.title}</td><td><span className="badge badge-blue">{p.service_track}</span></td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Active Campaigns</h3></div>
        <div className="table-scroll-wrapper">
          {loading ? <div className="card-body text-tertiary">Loading campaigns...</div> : error ? (
            <div className="empty-state"><AlertTriangle size={28} /><h3>Error</h3><p>{error}</p></div>
          ) : campaigns.length === 0 ? (
            <div className="empty-state"><Target size={28} /><h3>No campaigns yet</h3><p>Use the form above to create and launch campaigns.</p></div>
          ) : (
            <table className="data-table">
              <thead><tr><th>Name</th><th>Status</th><th>Leads</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id}>
                    <td className="font-semibold text-primary clickable" onClick={() => openDetail(c.id)}>{c.name || `Campaign ${c.id}`}</td>
                    <td><span className={`badge badge-${statusBadge(c.status)}`}>{c.status || "unknown"}</span></td>
                    <td>{c.lead_count ?? c.leads_count ?? "-"}</td>
                    <td className="nowrap text-sm text-secondary">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "-"}</td>
                    <td className="nowrap">
                      <div className="flex gap-1">
                        {String(c.status).toUpperCase() !== "ACTIVE" && <button className="btn btn-ghost btn-xs" style={{ color: "#16a34a" }} onClick={() => campaignAction(c.id, "START")} title="Start">Start</button>}
                        {String(c.status).toUpperCase() === "ACTIVE" && <button className="btn btn-ghost btn-xs" style={{ color: "#d97706" }} onClick={() => campaignAction(c.id, "PAUSED")} title="Pause">Pause</button>}
                        <button className="btn btn-ghost btn-xs" style={{ color: "#dc2626" }} onClick={() => campaignAction(c.id, "delete")} title="Delete">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {abTest && (abTest.variant_a.sent > 0 || abTest.variant_b.sent > 0) && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            <h3>A/B Test Results</h3>
            {!abTest.sample_size_sufficient && <span className="badge badge-amber" style={{ fontSize: 11 }}>Insufficient sample (&lt;30 per variant)</span>}
          </div>
          <div className="card-body">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {["A", "B"].map(v => {
                const d = v === "A" ? abTest.variant_a : abTest.variant_b;
                const other = v === "A" ? abTest.variant_b : abTest.variant_a;
                const isWinner = abTest.sample_size_sufficient && d.positive_rate > other.positive_rate && d.sent >= 30;
                return (
                  <div key={v} style={{ padding: 16, borderRadius: 8, border: `2px solid ${isWinner ? "#16a34a" : "var(--border)"}`, background: isWinner ? "var(--green-bg, #f0fdf4)" : "var(--bg-secondary, #f9fafb)" }}>
                    <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                      <span className="font-bold" style={{ fontSize: 16 }}>Variant {v}</span>
                      {isWinner && <span className="badge badge-green">Winner</span>}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      {[["Sent", d.sent], ["Open Rate", d.open_rate + "%"], ["Reply Rate", d.reply_rate + "%"], ["Positive Rate", d.positive_rate + "%"]].map(([label, val]) => (
                        <div key={label}>
                          <div className="text-xs text-tertiary">{label}</div>
                          <div className="font-bold" style={{ fontSize: 18 }}>{val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ============ SETTINGS ============
function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [routing, setRouting] = useState([]);
  const [tab, setTab] = useState("general");
  const [showToast, toastEl] = useToast();

  useEffect(() => {
    API.get("/settings").then(r => setSettings(r.data)).catch(() => {});
    API.get("/routing").then(r => setRouting(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    await API.put("/settings", settings);
    showToast("Settings saved", "success");
  };

  const updateRouting = async (id, field, value) => {
    try {
      await API.put(`/routing/${id}`, { [field]: value });
      const { data } = await API.get("/routing");
      setRouting(data);
      showToast("Routing updated", "success");
    } catch { showToast("Failed to update", "error"); }
  };

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header"><h2>Settings</h2></div>

      <div className="tab-bar">
        {["general","routing","digest"].map(t => (
          <div key={t} className={`tab ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t === "general" ? "General" : t === "routing" ? "Routing" : "Digest Preview"}
          </div>
        ))}
      </div>

      {tab === "general" && (
        <div className="card">
          <div className="card-body">
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Calendly URL</label>
                <input className="form-input" value={settings.calendly_url || ""} onChange={e => setSettings(s => ({ ...s, calendly_url: e.target.value }))} placeholder="https://calendly.com/..." />
              </div>
              <div className="form-group">
                <label className="form-label">Max Emails / Day</label>
                <input className="form-input" type="number" value={settings.max_emails_per_day || ""} onChange={e => setSettings(s => ({ ...s, max_emails_per_day: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">SQL Score Threshold</label>
                <input className="form-input" type="number" value={settings.sql_threshold || ""} onChange={e => setSettings(s => ({ ...s, sql_threshold: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Hot Lead Threshold</label>
                <input className="form-input" type="number" value={settings.hot_threshold || ""} onChange={e => setSettings(s => ({ ...s, hot_threshold: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Sending Persona Name</label>
                <input className="form-input" value={settings.sending_persona_name || ""} onChange={e => setSettings(s => ({ ...s, sending_persona_name: e.target.value }))} placeholder="Mariam Nasser" />
              </div>
              <div className="form-group">
                <label className="form-label">Sending Persona Title</label>
                <input className="form-input" value={settings.sending_persona_title || ""} onChange={e => setSettings(s => ({ ...s, sending_persona_title: e.target.value }))} placeholder="Client Advisory" />
              </div>
            </div>
            <button className="btn btn-primary mt-4" onClick={save}>Save Settings</button>
          </div>
        </div>
      )}

      {tab === "routing" && (
        <div className="card">
          <div className="card-header"><h3>Department Head Routing</h3></div>
          <table className="data-table">
            <thead><tr><th>Track</th><th>Scope</th><th>Assigned To</th><th>Email</th><th>Active</th></tr></thead>
            <tbody>
              {routing.map(r => (
                <tr key={r.id}>
                  <td><span className="badge badge-navy">{r.track}</span></td>
                  <td className="text-sm">{r.scope}</td>
                  <td className="text-sm font-medium">{r.assigned_name}</td>
                  <td className="text-sm text-secondary">{r.assigned_email}</td>
                  <td>
                    <span className={`badge badge-${r.active ? "green" : "gray"}`} style={{ cursor: "pointer" }} onClick={() => updateRouting(r.id, "active", r.active ? 0 : 1)}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
              {routing.length === 0 && <tr><td colSpan={5}><div className="empty-state"><p>No routing config found</p></div></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "digest" && <DigestPreview />}
    </div>
  );
}

function DigestPreview() {
  const [digest, setDigest] = useState(null);
  useEffect(() => { API.get("/digest/preview").then(r => setDigest(r.data)).catch(() => {}); }, []);

  if (!digest) return <div className="card"><div className="card-body"><p className="text-tertiary">Loading digest preview...</p></div></div>;

  return (
    <div className="card">
      <div className="card-header"><h3>Daily Digest Preview ({digest.date})</h3></div>
      <div className="card-body">
        <div className="grid-4 mb-4">
          <div style={{ padding: 12, background: "var(--bg-primary)", borderRadius: "var(--radius)" }}>
            <div className="text-xs text-tertiary">Emails Sent</div>
            <div className="font-bold text-lg">{digest.emails_sent_yesterday}</div>
          </div>
          <div style={{ padding: 12, background: "var(--bg-primary)", borderRadius: "var(--radius)" }}>
            <div className="text-xs text-tertiary">Replies</div>
            <div className="font-bold text-lg">{digest.replies_yesterday}</div>
          </div>
          <div style={{ padding: 12, background: "var(--bg-primary)", borderRadius: "var(--radius)" }}>
            <div className="text-xs text-tertiary">Meetings Booked</div>
            <div className="font-bold text-lg">{digest.meetings_booked_yesterday}</div>
          </div>
          <div style={{ padding: 12, background: "var(--bg-primary)", borderRadius: "var(--radius)" }}>
            <div className="text-xs text-tertiary">Pipeline</div>
            <div className="font-bold text-lg">AED {(digest.total_pipeline || 0).toLocaleString()}</div>
          </div>
        </div>
        {(digest.recent_replies || []).length > 0 && (
          <>
            <h4 className="text-sm font-semibold mb-3">Yesterday's Replies</h4>
            {digest.recent_replies.map(r => (
              <div key={r.id} className="flex items-center gap-3 mb-2" style={{ padding: 10, background: "var(--bg-primary)", borderRadius: "var(--radius)" }}>
                <span className="font-medium text-sm">{r.first_name} {r.last_name}</span>
                <span className="text-xs text-tertiary">{r.company}</span>
                {r.classification && <span className={`badge badge-${r.classification === 'INTERESTED' ? 'green' : r.classification === 'QUESTION' ? 'amber' : 'gray'}`}>{r.classification}</span>}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ============ USER MANAGEMENT ============
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin" });
  const [showToast, toastEl] = useToast();

  const load = async () => {
    try { const { data } = await API.get("/users"); setUsers(data || []); } catch { setUsers([]); }
  };

  useEffect(() => { load(); }, []);

  const addUser = async () => {
    try {
      await API.post("/users", form);
      showToast("User created", "success");
      setForm({ name: "", email: "", password: "", role: "admin" });
      setShowForm(false);
      load();
    } catch (e) { showToast("Failed to create user", "error"); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await API.delete(`/users/${id}`); showToast("User deleted", "success"); load(); }
    catch { showToast("Failed to delete", "error"); }
  };

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <h2>User Management</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ Add User"}
        </button>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" onClick={addUser}>Create User</button>
          </div>
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td className="font-semibold text-primary">{u.name}</td>
                <td className="text-secondary">{u.email}</td>
                <td><span className={`badge badge-${u.role === "admin" ? "navy" : "gray"}`}>{u.role}</span></td>
                <td className="text-sm text-tertiary">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</td>
                <td><button className="btn btn-ghost btn-xs text-red" onClick={() => deleteUser(u.id)}>Delete</button></td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5}><div className="empty-state"><UserCog size={28} /><p>No users found</p></div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ REPLIES ============
function Replies() {
  const [data, setData] = useState({ replies: [], pending_count: 0 });
  const [tab, setTab] = useState("pending");
  const [showToast, toastEl] = useToast();
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmHandoff, setConfirmHandoff] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    API.get("/replies").then(r => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = data.replies.filter(r => {
    if (tab === "pending") return r.draft_status === "pending" || r.draft_status === "edited" || (!r.draft_status && r.prospect_status !== "handed_off");
    if (tab === "handed_off") return r.prospect_status === "handed_off" || r.draft_status === "handed_off";
    return true;
  });

  const approveDraft = async (draftId) => {
    if (!draftId) return showToast("No draft to approve", "error");
    setActionLoading(draftId);
    try {
      // SN104 Block 5: this route creates an Outlook DRAFT and sends nothing (Block 4 re-pointed
      // it at m365.createReplyDraft). The old toast said "Reply sent successfully", which is the
      // failure mode that leaves replies unanswered: told it was sent, nobody opens Outlook.
      // Prefer the server's own message so the UI and the API cannot drift apart about what
      // happened; the literal is only a fallback for an older server build.
      const res = await API.post(`/replies/${draftId}/approve`);
      showToast(res?.data?.message ||
        "Draft created in Mariam's Outlook Drafts. Nothing sent yet — open Outlook to review and send.",
        "success");
      load();
    } catch (err) { showToast(err.response?.data?.error || "Failed to create draft", "error"); }
    finally { setActionLoading(null); }
  };

  const saveDraft = async (draftId) => {
    setActionLoading(draftId);
    try {
      await API.post(`/replies/${draftId}/edit`, { draft_text: editText });
      showToast("Draft saved", "success");
      setEditingId(null);
      load();
    } catch (err) { showToast(err.response?.data?.error || "Failed to save", "error"); }
    finally { setActionLoading(null); }
  };

  const handoff = async (prospectId) => {
    setActionLoading("handoff-" + prospectId);
    try {
      const { data: r } = await API.post(`/prospects/${prospectId}/handoff`);
      showToast(`Handed off to ${r.handed_off_to}`, "success");
      setConfirmHandoff(null);
      load();
    } catch (err) { showToast(err.response?.data?.error || "Handoff failed", "error"); }
    finally { setActionLoading(null); }
  };

  const regenerateDraft = async (draftId) => {
    setActionLoading("regen-" + draftId);
    try {
      const { data: r } = await API.post(`/replies/${draftId}/regenerate`);
      showToast("Draft regenerated", "success");
      load();
    } catch (err) { showToast(err.response?.data?.error || "Regeneration failed", "error"); }
    finally { setActionLoading(null); }
  };

  const CLASSIFICATION_BADGE = { INTERESTED: "badge-green", QUESTION: "badge-blue", NOT_NOW: "badge-amber", NOT_INTERESTED: "badge-red", UNSUBSCRIBE: "badge-red", OUT_OF_OFFICE: "badge-gray" };

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div><h2>Replies</h2><p className="text-sm text-secondary">{data.pending_count} pending review</p></div>
      </div>

      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {[["pending", "Pending Review"], ["handed_off", "Handed Off"], ["all", "All"]].map(([key, label]) => (
          <div key={key} className={`tab ${tab === key ? "active" : ""}`} onClick={() => setTab(key)}>{label}{key === "pending" && data.pending_count > 0 ? ` (${data.pending_count})` : ""}</div>
        ))}
      </div>

      {filtered.length === 0 && <div className="card"><div className="empty-state"><MessageSquare size={28} /><h3>No replies</h3><p>{tab === "pending" ? "No replies pending review" : "No replies found"}</p></div></div>}

      <div className="flex-col gap-4">
        {filtered.map(r => (
          <div key={r.activity_id} className="card" style={{ overflow: "hidden" }}>
            <div className="card-body">
              <div className="flex items-center gap-2" style={{ marginBottom: 8, flexWrap: "wrap" }}>
                <span className="font-semibold text-primary" style={{ cursor: "pointer" }} onClick={() => navigate(`/prospects/${r.prospect_id}`)}>{r.first_name} {r.last_name}</span>
                <span className="text-sm text-secondary">{r.title} at {r.company}</span>
                <span className="badge badge-navy" style={{ fontSize: 10 }}>{r.service_track}</span>
                {r.classification && <span className={`badge ${CLASSIFICATION_BADGE[r.classification] || "badge-gray"}`} style={{ fontSize: 10 }}>{r.classification}</span>}
                {r.prospect_status === "handed_off" && <span className="badge badge-amber" style={{ fontSize: 10 }}>Handed Off</span>}
                {r.draft_status === "sent" && <span className="badge badge-green" style={{ fontSize: 10 }}>Sent</span>}
                <span className="text-xs text-tertiary" style={{ marginLeft: "auto" }}>{new Date(r.replied_at).toLocaleString()}</span>
              </div>

              <div style={{ background: "var(--bg-hover, #f9fafb)", padding: "10px 14px", borderRadius: 6, marginBottom: 10, borderLeft: "3px solid #16a34a" }}>
                <div className="text-xs text-tertiary font-medium" style={{ marginBottom: 4 }}>Their Reply</div>
                <div className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{r.reply_text || "No reply text captured"}</div>
              </div>

              {r.draft_text && editingId !== r.draft_id && (
                <div style={{ background: "#f0fdf4", padding: "10px 14px", borderRadius: 6, marginBottom: 10, borderLeft: "3px solid #2563eb" }}>
                  <div className="text-xs text-tertiary font-medium" style={{ marginBottom: 4 }}>AI Draft Response</div>
                  <div className="text-sm" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{r.draft_text}</div>
                </div>
              )}

              {editingId === r.draft_id && (
                <div style={{ marginBottom: 10 }}>
                  <textarea className="form-control" rows={5} value={editText} onChange={e => setEditText(e.target.value)} style={{ width: "100%", fontFamily: "inherit", fontSize: 13 }} />
                  <div className="flex gap-2" style={{ marginTop: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => saveDraft(r.draft_id)} disabled={actionLoading === r.draft_id}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </div>
              )}

              {r.prospect_status !== "handed_off" && r.draft_status !== "sent" && (
                <div className="flex gap-2" style={{ marginTop: 8 }}>
                  {r.draft_id && r.draft_text && editingId !== r.draft_id && (
                    <button className="btn btn-primary btn-sm" style={{ background: "#16a34a", borderColor: "#16a34a" }} onClick={() => approveDraft(r.draft_id)} disabled={actionLoading === r.draft_id}><Mail size={12} /> Approve & Create Draft</button>
                  )}
                  {r.draft_id && editingId !== r.draft_id && (
                    <button className="btn btn-secondary btn-sm" onClick={() => { setEditingId(r.draft_id); setEditText(r.draft_text || ""); }}>Edit Draft</button>
                  )}
                  {r.draft_id && editingId !== r.draft_id && (
                    <button className="btn btn-secondary btn-sm" onClick={() => regenerateDraft(r.draft_id)} disabled={actionLoading === "regen-" + r.draft_id}><RefreshCw size={12} className={actionLoading === "regen-" + r.draft_id ? "spin" : ""} /> Regenerate</button>
                  )}
                  <button className="btn btn-secondary btn-sm" style={{ color: "#d97706", borderColor: "#d97706" }} onClick={() => setConfirmHandoff(r.prospect_id)} disabled={actionLoading === "handoff-" + r.prospect_id}><UserCheck size={12} /> Take Over</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {confirmHandoff && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
          <div className="card" style={{ maxWidth: 400, width: "90%" }}>
            <div className="card-body" style={{ textAlign: "center" }}>
              <AlertTriangle size={32} style={{ color: "#d97706", marginBottom: 12 }} />
              <h3>Confirm Handoff</h3>
              <p className="text-sm text-secondary" style={{ margin: "8px 0 16px" }}>This will pause all automated sequences and notify the department head. The lead will be removed from the sales engine automation.</p>
              <div className="flex gap-2" style={{ justifyContent: "center" }}>
                <button className="btn btn-primary btn-sm" style={{ background: "#d97706", borderColor: "#d97706" }} onClick={() => handoff(confirmHandoff)} disabled={actionLoading === "handoff-" + confirmHandoff}>Confirm Handoff</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirmHandoff(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ ANALYTICS ============
function Analytics() {
  const [overview, setOverview] = useState(null);
  const [emailPerf, setEmailPerf] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, toastEl] = useToast();

  const TRACK_LABELS = { C: "Consultancy", I: "Investments", A: "Accounting", T: "Transaction Advisory", F: "Family Office", E: "Execution" };
  const GEO_LABELS = { "1": "UAE", "2": "GCC", "3": "Lebanon", "4": "Africa", "5": "Other" };

  useEffect(() => {
    Promise.all([
      API.get("/analytics/overview").then(r => setOverview(r.data)).catch(() => {}),
      API.get("/analytics/email-performance").then(r => setEmailPerf(r.data)).catch(() => {}),
      API.get("/analytics/mailbox-health").then(r => setMailboxes(r.data)).catch(() => setMailboxes([]))
    ]).finally(() => setLoading(false));
  }, []);

  const exportCSV = () => {
    if (!overview) return;
    let csv = "Section,Metric,Value\n";
    // Track performance
    (overview.by_track || []).forEach(t => {
      csv += `Track,${t.track} - ${TRACK_LABELS[t.track] || t.track} Count,${t.count}\n`;
      csv += `Track,${t.track} Open Rate,${t.open_rate}%\n`;
      csv += `Track,${t.track} Reply Rate,${t.reply_rate}%\n`;
    });
    // Email steps
    emailPerf.forEach(s => {
      csv += `Email Step,Step ${s.step} Sent,${s.sent}\n`;
      csv += `Email Step,Step ${s.step} Open Rate,${s.open_rate}%\n`;
      csv += `Email Step,Step ${s.step} Reply Rate,${s.reply_rate}%\n`;
    });
    // Funnel
    if (overview.funnel) {
      Object.entries(overview.funnel).forEach(([k, v]) => { csv += `Funnel,${k},${v}\n`; });
    }
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "analytics-" + new Date().toISOString().slice(0, 10) + ".csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="main-content"><div className="card"><div className="card-body text-tertiary">Loading analytics...</div></div></div>;

  const f = overview?.funnel || {};
  const funnelStages = [
    { label: "Imported", count: f.imported || 0, color: "#9ca3af" },
    { label: "Generated", count: f.generated || 0, color: "#3b82f6" },
    { label: "Sent", count: f.sequence_active || 0, color: "#0d9488" },
    { label: "Replied", count: f.replied || 0, color: "#16a34a" },
    { label: "Meeting", count: f.meeting_scheduled || 0, color: "#059669" },
    { label: "Handed Off", count: f.handed_off || 0, color: "#d97706" }
  ];
  const maxFunnel = Math.max(...funnelStages.map(s => s.count), 1);

  const ts = overview?.time_series || [];
  const maxDay = Math.max(...ts.map(d => (d.sent || 0) + (d.replied || 0) + (d.bounced || 0)), 1);

  const bestReplyStep = emailPerf.length > 0 ? emailPerf.reduce((best, s) => s.reply_rate > best.reply_rate ? s : best, emailPerf[0]).step : null;

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div><h2>Analytics</h2><p className="text-sm text-secondary">Campaign performance overview</p></div>
        <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={14} /> Export CSV</button>
      </div>

      {/* Section 1: Conversion Funnel */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3>Conversion Funnel</h3>{overview?.avg_time_to_reply != null && <span className="text-sm text-secondary">Avg time to reply: {overview.avg_time_to_reply} days</span>}</div>
        <div className="card-body">
          {funnelStages.map((stage, i) => {
            const prev = i > 0 ? funnelStages[i - 1].count : stage.count;
            const pct = prev > 0 ? Math.round((stage.count / prev) * 100) : 0;
            return (
              <div key={stage.label} style={{ marginBottom: 10 }}>
                <div className="flex items-center gap-2" style={{ marginBottom: 3 }}>
                  <span className="text-sm font-medium" style={{ width: 90 }}>{stage.label}</span>
                  <span className="text-sm font-bold">{stage.count}</span>
                  {i > 0 && <span className="text-xs text-tertiary">({pct}% of {funnelStages[i - 1].label})</span>}
                </div>
                <div style={{ height: 20, background: "var(--bg-secondary, #f1f5f9)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.max((stage.count / maxFunnel) * 100, 1)}%`, background: stage.color, borderRadius: 4, transition: "width 0.5s" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 2: 30-Day Activity */}
      {ts.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3>30-Day Activity</h3></div>
          <div className="card-body">
            <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 200, overflow: "hidden" }}>
              {ts.map((d, i) => {
                const sent = d.sent || 0;
                const replied = d.replied || 0;
                const bounced = d.bounced || 0;
                const total = sent + replied + bounced;
                const h = (v) => Math.max((v / maxDay) * 180, v > 0 ? 2 : 0);
                return (
                  <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", minWidth: 0 }} title={`${d.date}\nSent: ${sent} | Replied: ${replied} | Bounced: ${bounced}`}>
                    {bounced > 0 && <div style={{ width: "80%", height: h(bounced), background: "#dc2626", borderRadius: "2px 2px 0 0" }} />}
                    {replied > 0 && <div style={{ width: "80%", height: h(replied), background: "#16a34a" }} />}
                    {sent > 0 && <div style={{ width: "80%", height: h(sent), background: "#3b82f6", borderRadius: bounced || replied ? "0" : "2px 2px 0 0" }} />}
                    {i % 5 === 0 && <div className="text-xs text-tertiary" style={{ marginTop: 4, fontSize: 9, whiteSpace: "nowrap" }}>{d.date.slice(5)}</div>}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4" style={{ marginTop: 8, justifyContent: "center" }}>
              {[["Sent", "#3b82f6"], ["Replied", "#16a34a"], ["Bounced", "#dc2626"]].map(([l, c]) => (
                <div key={l} className="flex items-center gap-1 text-xs text-secondary"><div style={{ width: 10, height: 10, borderRadius: 2, background: c }} />{l}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Section 3: Track Performance */}
      {(overview?.by_track || []).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3>Track Performance</h3></div>
          <div className="table-scroll-wrapper">
            <table className="data-table">
              <thead><tr><th>Track</th><th>Label</th><th>Prospects</th><th>Open Rate</th><th>Reply Rate</th></tr></thead>
              <tbody>
                {overview.by_track.map(t => (
                  <tr key={t.track}>
                    <td><span className="badge badge-navy">{t.track}</span></td>
                    <td>{TRACK_LABELS[t.track] || t.track}</td>
                    <td className="font-bold">{t.count}</td>
                    <td>{t.open_rate}%</td>
                    <td><span style={{ color: t.reply_rate >= 8 ? "#16a34a" : t.reply_rate >= 4 ? "#d97706" : "#dc2626", fontWeight: 600 }}>{t.reply_rate}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section 3b: Geo Performance */}
      {(overview?.by_geo || []).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3>Geography Performance</h3></div>
          <div className="table-scroll-wrapper">
            <table className="data-table">
              <thead><tr><th>Geo Tier</th><th>Region</th><th>Prospects</th><th>Reply Rate</th></tr></thead>
              <tbody>
                {overview.by_geo.map(g => (
                  <tr key={g.geo}>
                    <td><span className="badge badge-blue">Tier {g.geo}</span></td>
                    <td>{GEO_LABELS[g.geo] || "Other"}</td>
                    <td className="font-bold">{g.count}</td>
                    <td><span style={{ color: g.reply_rate >= 8 ? "#16a34a" : g.reply_rate >= 4 ? "#d97706" : "#dc2626", fontWeight: 600 }}>{g.reply_rate}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section 4: Email Step Performance */}
      {emailPerf.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
          {emailPerf.map(s => (
            <div key={s.step} className="card" style={{ border: s.step === bestReplyStep ? "2px solid #16a34a" : undefined }}>
              <div className="card-body" style={{ textAlign: "center" }}>
                <div className="text-sm text-secondary font-medium">Email {s.step}</div>
                <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{s.sent}</div>
                <div className="text-xs text-tertiary">prospects reached</div>
                <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
                  <div><div className="text-xs text-tertiary">Open</div><div className="font-bold">{s.open_rate}%</div></div>
                  <div><div className="text-xs text-tertiary">Reply</div><div className="font-bold" style={{ color: s.step === bestReplyStep ? "#16a34a" : "inherit" }}>{s.reply_rate}%</div></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Section 5: Top Subject Lines */}
      {(overview?.top_subjects || []).length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3>Top Subject Lines</h3></div>
          <div className="table-scroll-wrapper">
            <table className="data-table">
              <thead><tr><th>Subject Line</th><th>Sent</th><th>Open Rate</th></tr></thead>
              <tbody>
                {overview.top_subjects.map((s, i) => (
                  <tr key={i}>
                    <td className="text-sm" title={s.subject}>{(s.subject || "").length > 60 ? s.subject.substring(0, 60) + "..." : s.subject}</td>
                    <td>{s.sent}</td>
                    <td className="font-bold">{s.sent > 0 ? Math.round((s.opens / s.sent) * 100) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section 6: Mailbox Health */}
      {mailboxes.length > 0 && (
        <div className="card">
          <div className="card-header"><h3>Mailbox Health</h3></div>
          <div className="card-body">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
              {mailboxes.map((m, i) => {
                const rep = m.warmup_reputation || 0;
                const color = rep >= 90 ? "#16a34a" : rep >= 70 ? "#d97706" : "#dc2626";
                const badgeClass = rep >= 90 ? "badge-green" : rep >= 70 ? "badge-amber" : "badge-red";
                const emailShort = m.email.length > 25 ? m.email.split("@")[0] + "@..." : m.email;
                return (
                  <div key={i} style={{ padding: 14, borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-secondary, #f9fafb)" }}>
                    <div className="text-sm font-medium" title={m.email} style={{ marginBottom: 6 }}>{emailShort}</div>
                    <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
                      <span className="text-xs text-tertiary">Reputation:</span>
                      <span className="font-bold" style={{ color }}>{rep}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge ${badgeClass}`} style={{ fontSize: 10 }}>{rep >= 90 ? "Healthy" : rep >= 70 ? "Fair" : "Low"}</span>
                      {m.warmup_enabled && <span className="badge badge-blue" style={{ fontSize: 10 }}>Warmup On</span>}
                      {m.is_smtp_success && m.is_imap_success && <span className="badge badge-green" style={{ fontSize: 10 }}>Connected</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ OUTREACH ============
function Outreach() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tagFilter, setTagFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [trackFilter, setTrackFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [showToast, toastEl] = useToast();

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (tagFilter !== "all") params.set("tag", tagFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (trackFilter !== "all") params.set("track", trackFilter);
    if (search) params.set("search", search);
    params.set("page", page);
    params.set("limit", 100);
    API.get("/outreach?" + params.toString()).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [tagFilter, statusFilter, trackFilter, search, page]);

  useEffect(() => { loadData(); }, [loadData]);

  const doSync = async () => {
    setSyncing(true);
    try {
      await API.post("/smartlead/sync");
      showToast("Sync complete", "success");
      loadData();
    } catch(e) { showToast("Sync failed", "error"); }
    setSyncing(false);
  };

  return (
    <div className="main-content">
      {toastEl}
      <div className="page-header">
        <div>
          <h2>Outreach</h2>
          <div className="page-subtitle">All prospects in active outreach sequences</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm" onClick={doSync} disabled={syncing}>
            <RefreshCw size={14} /> {syncing ? "Syncing..." : "Sync Smartlead"}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={loadData}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {data?.summary && (
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card">
            <div className="stat-label">Active</div>
            <div className="stat-value">{data.summary.active || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Sent</div>
            <div className="stat-value">{data.summary.total_sent || 0}</div>
            <div className="stat-sub">{data.summary.total_sent > 0 ? ((data.summary.total_opened / data.summary.total_sent * 100).toFixed(1) + "% opened") : "No sends yet"}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Replied</div>
            <div className="stat-value">{data.summary.replied || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Meetings</div>
            <div className="stat-value">{data.summary.meetings || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Bounced</div>
            <div className="stat-value" style={{ color: data.summary.bounced > 0 ? "#dc2626" : undefined }}>{data.summary.bounced || 0}</div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div className="flex gap-2" style={{ flexWrap: "wrap", alignItems: "center" }}>
            <input className="form-input" style={{ maxWidth: 250 }} placeholder="Search name, company, email..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            <select className="form-select" style={{ maxWidth: 180 }} value={tagFilter} onChange={e => { setTagFilter(e.target.value); setPage(1); }}>
              <option value="all">All Tags</option>
              {(data?.tags || []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="form-select" style={{ maxWidth: 150 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">All Status</option>
              <option value="sequence_active">Active</option>
              <option value="replied">Replied</option>
              <option value="meeting_scheduled">Meeting</option>
              <option value="bounced">Bounced</option>
              <option value="generated">Generated</option>
              <option value="engaged">Engaged</option>
            </select>
            <select className="form-select" style={{ maxWidth: 120 }} value={trackFilter} onChange={e => { setTrackFilter(e.target.value); setPage(1); }}>
              <option value="all">All Tracks</option>
              <option value="C">C</option>
              <option value="I">I</option>
              <option value="A">A</option>
              <option value="T">T</option>
              <option value="F">F</option>
              <option value="E">E</option>
            </select>
            <span className="text-sm text-secondary">{data?.total || 0} prospects</span>
          </div>
        </div>
      </div>

      <div className="text-xs text-secondary" style={{ marginBottom: 8, display: "flex", gap: 16 }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#22c55e", marginRight: 4, verticalAlign: "middle" }}></span>Sent</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#3b82f6", marginRight: 4, verticalAlign: "middle" }}></span>Opened</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#e5e7eb", marginRight: 4, verticalAlign: "middle" }}></span>Not sent</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#dc2626", marginRight: 4, verticalAlign: "middle" }}></span>Bounced</span>
      </div>

      <div className="card">
        <div className="table-scroll-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Title</th>
                <th>Company</th>
                <th>Track</th>
                <th>Tag</th>
                <th>Status</th>
                <th>Score</th>
                <th style={{ textAlign: "center" }}>E1</th>
                <th style={{ textAlign: "center" }}>E2</th>
                <th style={{ textAlign: "center" }}>E3</th>
                <th style={{ textAlign: "center" }}>E4</th>
                <th>Reply</th>
                <th>Meeting</th>
              </tr>
            </thead>
            <tbody>
              {(data?.prospects || []).map(p => {
                const statusColor = {
                  sequence_active: "green", generated: "blue", engaged: "teal",
                  replied_interested: "green", replied_question: "blue", replied_not_now: "amber",
                  replied_not_interested: "red", meeting_scheduled: "purple", bounced: "red",
                  handed_off: "gray"
                }[p.status] || "gray";
                const statusLabel = p.status.replace("replied_", "").replace("sequence_", "").replace("_", " ");
                const replyBadge = p.status.startsWith("replied_") || p.status === "engaged";
                const replyLabel = p.status === "engaged" ? "ENGAGED" : p.status.replace("replied_", "").toUpperCase();

                return (
                  <tr key={p.id}>
                    <td className="font-semibold text-primary" style={{ cursor: "pointer" }} onClick={() => window.location.href = "/prospects/" + p.id}>
                      {p.first_name} {p.last_name}
                    </td>
                    <td className="text-sm text-secondary">{p.title}</td>
                    <td className="text-sm">{p.company}</td>
                    <td><span className={`badge badge-${p.service_track === "C" ? "blue" : p.service_track === "I" ? "purple" : p.service_track === "A" ? "green" : p.service_track === "T" ? "amber" : p.service_track === "E" ? "teal" : "pink"}`}>{p.service_track}</span></td>
                    <td className="text-xs text-secondary">{p.campaign_tag}</td>
                    <td><span className={`badge badge-${statusColor}`}>{statusLabel}</span></td>
                    <td className="text-sm">{p.lead_score || 0}</td>
                    {[1,2,3,4].map(n => (
                      <td key={n} style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-block", width: 12, height: 12, borderRadius: "50%",
                          background: p.status === 'bounced' ? '#dc2626' : (p.emails_sent || 0) >= n
                            ? ((p.emails_opened || 0) > 0 && n <= (p.emails_sent || 0) ? "#3b82f6" : "#22c55e")
                            : "#e5e7eb"
                        }} title={`Email ${n}: ${p.status === 'bounced' ? 'Bounced' : (p.emails_sent || 0) >= n ? "Sent" : "Not sent"}`} />
                      </td>
                    ))}
                    <td>{replyBadge ? <span className={`badge badge-${statusColor}`}>{replyLabel}</span> : ""}</td>
                    <td>{p.status === "meeting_scheduled" ? <Calendar size={16} style={{ color: "#8b5cf6" }} /> : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data?.pages > 1 && (
          <div className="card-body" style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span className="text-sm text-secondary">Page {data.page} of {data.pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ MAIN APP ============
export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (token) {
      API.get("/dashboard").then(() => setUser({ email: "admin" })).catch(() => clearToken()).finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const logout = () => { clearToken(); setUser(null); };

  if (checking) return null;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <BrowserRouter>
      <div className="app-layout">
        <button className="hamburger" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
        <div className={`overlay ${sidebarOpen ? "show" : ""}`} onClick={() => setSidebarOpen(false)} />
        <Sidebar user={user} onLogout={logout} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/prospects" element={<Prospects />} />
          <Route path="/prospects/:id" element={<ProspectDetail />} />
          <Route path="/replies" element={<Replies />} />
          <Route path="/contacts" element={<ContactsDB />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/outreach" element={<Outreach />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/meetings" element={<Meetings />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/ai-engine" element={<AIEngine />} />
          <Route path="/smart-import" element={<SmartImport />} />
          <Route path="/import" element={<Import />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
